import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { scenariosAPI, structuredInputsAPI, analyzerAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Switch } from '../../components/ui/switch';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { formatCurrency, formatNumber } from '../../lib/utils';
import {
  Building2, MapPin, Layers, DollarSign, HardHat, Receipt,
  CalendarClock, TrendingUp, Banknote, Calculator, Landmark,
  CheckCircle2, ChevronLeft, ChevronRight, Save, Play,
  Info, Plus, Trash2, ArrowLeft, Loader2, ShieldCheck, X, AlertTriangle, FileText,
  ChevronUp, ChevronDown
} from 'lucide-react';
import { AField, TableInput, WizardField, FormattedInput, LABEL_MAP, UNIT_MAP, EMPTY_OPTION_VALUE } from './helpers';
import { ExistingLiabilitiesPanel } from './ExistingLiabilitiesPanel';
import { MonthlyScheduleEditor } from '../../components/MonthlyScheduleEditor';
import { WaterfallPanel } from './WaterfallPanel';
import { WizardValidationBanner, buildIssues, useRunHorizon } from './WizardValidation';
import {
  PROJECT_LOAN_PRESETS,
  presetFromBridgeMode,
  REPAYMENT_MODES,
  INTEREST_TREATMENTS,
} from '../../lib/financing-taxonomy';

/* ── Market Hint: shows market suggestion alongside manual value ── */
function MarketHint({ suggestion, onApply }) {
  if (!suggestion || suggestion.suggested == null) return null;
  const diff = suggestion.diff_pct;
  const isCurrency = suggestion.label?.includes('Precio');
  const fmtVal = (v) => isCurrency ? `$${Number(v).toLocaleString('es-MX', { maximumFractionDigits: 0 })}` : Number(v).toLocaleString('es-MX', { maximumFractionDigits: 1 });

  return (
    <div className="flex items-center gap-2 px-2 py-1 rounded-md text-[10px]" style={{ background: 'var(--market-subtle)', border: '1px solid rgba(13,189,139,0.15)' }} data-testid="market-hint">
      <TrendingUp className="w-3 h-3 shrink-0" style={{ color: 'var(--market-primary)' }} />
      <span style={{ color: 'var(--text-secondary)' }}>
        Mercado: <strong style={{ color: 'var(--market-primary)' }}>{fmtVal(suggestion.suggested)}</strong>
        {diff != null && <span style={{ color: Math.abs(diff) > 15 ? 'var(--warning-color)' : 'var(--text-muted)' }}> ({diff > 0 ? '+' : ''}{diff}%)</span>}
      </span>
      <span className="text-[8px] px-1 py-0.5 rounded" style={{ background: suggestion.confidence === 'high' ? 'var(--success-soft)' : 'var(--warning-soft)', color: suggestion.confidence === 'high' ? 'var(--success)' : 'var(--warning-color)' }}>
        {suggestion.confidence === 'high' ? 'Alta' : 'Media'}
      </span>
      <button onClick={() => onApply(suggestion.suggested)} className="ml-auto font-semibold transition-colors hover:underline" style={{ color: 'var(--market-primary)' }}>
        Aplicar
      </button>
    </div>
  );
}

/* ── Typology Cost Hint: shows calculated cost from typologies ── */
function TypologyCostHint({ assumptions, structured, onChange }) {
  const typos = structured?.typologies || [];
  const valid = typos.filter(t => (t.quantity || 0) > 0 && (t.direct_cost_per_unit || 0) > 0);
  if (valid.length === 0) return null;

  const totalQ = valid.reduce((s, t) => s + (t.quantity || 0), 0);
  const weightedCost = valid.reduce((s, t) => s + (t.quantity || 0) * (t.direct_cost_per_unit || 0), 0) / totalQ;
  const currentCost = assumptions?.hard_cost_per_unit || 0;
  const diff = currentCost > 0 ? ((currentCost - weightedCost) / weightedCost * 100) : 0;
  const isOverride = Math.abs(diff) > 2;

  if (!isOverride) return null;

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[10px]" style={{ background: 'var(--warning-soft)', border: '1px solid rgba(245,166,35,0.2)' }} data-testid="typology-cost-hint">
      <AlertTriangle className="w-3 h-3 shrink-0" style={{ color: 'var(--warning-color)' }} />
      <span style={{ color: 'var(--text-secondary)' }}>
        Tipologías: <strong>${Math.round(weightedCost).toLocaleString('es-MX')}</strong>
        <span style={{ color: 'var(--warning-color)' }}> (difiere {diff > 0 ? '+' : ''}{diff.toFixed(0)}%)</span>
      </span>
      <button onClick={() => onChange('hard_cost_per_unit', weightedCost)} className="ml-auto font-semibold transition-colors hover:underline" style={{ color: 'var(--financial-primary)' }}>
        Usar tipologías
      </button>
    </div>
  );
}

/* ── Typology Price Hint: shows calculated price from typologies ── */
function TypologyPriceHint({ assumptions, structured, onChange }) {
  const typos = structured?.typologies || [];
  const valid = typos.filter(t => (t.quantity || 0) > 0 && (t.list_price || 0) > 0);
  if (valid.length === 0) return null;

  const totalQ = valid.reduce((s, t) => s + (t.quantity || 0), 0);
  const weightedPrice = valid.reduce((s, t) => s + (t.quantity || 0) * (t.list_price || 0), 0) / totalQ;
  const currentPrice = assumptions?.avg_price_per_unit || 0;
  const diff = currentPrice > 0 ? ((currentPrice - weightedPrice) / weightedPrice * 100) : 0;
  const isOverride = Math.abs(diff) > 2;

  if (!isOverride) return null;

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[10px]" style={{ background: isOverride ? 'var(--warning-soft)' : 'var(--financial-subtle)', border: `1px solid ${isOverride ? 'rgba(245,166,35,0.2)' : 'rgba(99,91,255,0.1)'}` }} data-testid="typology-price-hint">
      <AlertTriangle className="w-3 h-3 shrink-0" style={{ color: 'var(--warning-color)' }} />
      <span style={{ color: 'var(--text-secondary)' }}>
        Tipologías: <strong>${Math.round(weightedPrice).toLocaleString('es-MX')}</strong>
        <span style={{ color: 'var(--warning-color)' }}> (tu precio difiere {diff > 0 ? '+' : ''}{diff.toFixed(0)}%)</span>
      </span>
      <button onClick={() => onChange('avg_price_per_unit', weightedPrice)} className="ml-auto font-semibold transition-colors hover:underline" style={{ color: 'var(--financial-primary)' }}>
        Usar tipologías
      </button>
    </div>
  );
}

/* ── Typology Absorption Hint: shows calculated absorption from typologies ── */
function TypologyAbsorptionHint({ assumptions, structured, onChange }) {
  const typos = structured?.typologies || [];
  const valid = typos.filter(t => (t.quantity || 0) > 0 && (t.absorption_units_month || 0) > 0);
  if (valid.length === 0) return null;

  const totalAbsorption = valid.reduce((s, t) => s + (t.absorption_units_month || 0), 0);
  const currentAbsorption = assumptions?.absorption_rate_units_per_month || 0;
  const diff = currentAbsorption > 0 ? ((currentAbsorption - totalAbsorption) / totalAbsorption * 100) : 0;
  const isOverride = Math.abs(diff) > 5;

  if (!isOverride) return null;

  return (
    <div className="flex items-center gap-2 px-2 py-1.5 rounded-md text-[10px]" style={{ background: 'var(--warning-soft)', border: '1px solid rgba(245,166,35,0.2)' }} data-testid="typology-absorption-hint">
      <AlertTriangle className="w-3 h-3 shrink-0" style={{ color: 'var(--warning-color)' }} />
      <span style={{ color: 'var(--text-secondary)' }}>
        Tipologías: <strong>{totalAbsorption.toFixed(1)} uds/mes</strong>
        <span style={{ color: 'var(--warning-color)' }}> (tu absorción difiere {diff > 0 ? '+' : ''}{diff.toFixed(0)}%)</span>
      </span>
      <button onClick={() => onChange('absorption_rate_units_per_month', Math.round(totalAbsorption * 10) / 10)} className="ml-auto font-semibold transition-colors hover:underline" style={{ color: 'var(--financial-primary)' }} data-testid="sync-absorption-btn">
        Usar tipologías
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   STEP DEFINITIONS
   ════════════════════════════════════════════════════════════════ */
export const STEPS = [
  { id: 'general',       label: 'Datos Generales',     shortLabel: 'General',      icon: Building2,    category: 'proyecto' },
  { id: 'typologies',    label: 'Tipologías',          shortLabel: 'Tipologías',   icon: Layers,       category: null },
  { id: 'land',          label: 'Terreno e Iniciales',  shortLabel: 'Terreno',      icon: MapPin,       category: 'terreno' },
  { id: 'budget',        label: 'Presupuesto',          shortLabel: 'Presupuesto',  icon: HardHat,      category: 'construccion' },
  { id: 'indirect',      label: 'Gastos Indirectos',    shortLabel: 'Indirectos',   icon: Receipt,      category: 'indirectos' },
  { id: 'schedule',      label: 'Cronograma',           shortLabel: 'Cronograma',   icon: CalendarClock,category: 'cronograma' },
  { id: 'sales',         label: 'Ventas',               shortLabel: 'Ventas',       icon: TrendingUp,   category: 'ventas' },
  { id: 'collection',    label: 'Cobranza',             shortLabel: 'Cobranza',     icon: Banknote,     category: 'cobranza' },
  { id: 'financing',     label: 'Financiamiento',       shortLabel: 'Financiamiento', icon: Landmark,   category: 'financiamiento' },
  { id: 'assumptions',   label: 'Supuestos Generales',  shortLabel: 'Supuestos',    icon: Calculator,   category: 'general' },
  { id: 'review',        label: 'Revisión Final',       shortLabel: 'Revisión',     icon: CheckCircle2, category: null },
];

/* ── Step Components (imported from helpers.jsx: AField, TableInput, WizardField) ── */

export function StepGeneral({ assumptions, onChange, structured }) {
  // Auto-compute horizon from schedule fields
  const presale = assumptions?.presale_months || 0;
  const constr = assumptions?.construction_months || 0;
  const post = assumptions?.post_construction_months || 0;
  const computedHorizon = presale + constr + post;

  // Auto-compute areas from typologies
  const typos = structured?.typologies || [];
  const computedConstruction = typos.reduce((s, t) => s + (t.area_m2 || 0) * (t.quantity || 0), 0);
  const computedLot = typos.reduce((s, t) => s + (t.terrace_m2 || 0) * (t.quantity || 0), 0);
  const computedUnits = typos.reduce((s, t) => s + (t.quantity || 0), 0);

  return (
    <div className="space-y-5">
      {/* Ficha del proyecto */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Ficha del proyecto</h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
          <AField aKey="project_name" assumptions={assumptions} onChange={onChange} type="text" className="md:col-span-2" />
          <AField aKey="project_location" assumptions={assumptions} onChange={onChange} type="text" />
          <AField aKey="project_type" assumptions={assumptions} onChange={onChange} required placeholder="Selecciona tipo..." options={[
            ['residencial','Residencial'],['comercial','Comercial'],['industrial','Industrial'],
            ['mixto','Mixto'],['equipamiento','Equipamiento'],['hotelero','Hotelero'],['oficinas','Oficinas']
          ]} />
          <AField aKey="housing_format" assumptions={assumptions} onChange={onChange} required placeholder="Selecciona formato..." options={[
            ['vertical','Vertical'],['horizontal','Horizontal'],['mixto','Mixto']
          ]} />
          <AField aKey="market_segment" assumptions={assumptions} onChange={onChange} required placeholder="Selecciona segmento..." options={[
            ['interes_social','Interés Social'],['economico','Económico'],['medio','Medio'],
            ['residencial','Residencial'],['residencial_plus','Residencial Plus'],['premium','Premium']
          ]} />
          <AField aKey="currency" assumptions={assumptions} onChange={onChange} options={[['MXN','MXN'],['USD','USD'],['UDI','UDI']]} />
        </div>
      </div>

      {/* Superficies */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>Superficies</h5>
        {/* SWA-196 · urbanismo visible como ficha técnica (firmado 2-nov) */}
        <p className="text-[11px] mb-3" style={{ color: '#64748B' }} data-testid="superficies-informativo">
          Solo informativo · no entra al cálculo. La única que usa el motor es la superficie vendible, para la utilidad por m².
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
          <AField aKey="total_land_area_m2" assumptions={assumptions} onChange={onChange} />
          <AField aKey="total_buildable_area_m2" assumptions={assumptions} onChange={onChange} />
          <AField aKey="total_saleable_area_m2" assumptions={assumptions} onChange={onChange} />
          <AField aKey="total_common_area_m2" assumptions={assumptions} onChange={onChange} />
          <AField aKey="total_floors" assumptions={assumptions} onChange={onChange} />
          <AField aKey="total_basements" assumptions={assumptions} onChange={onChange} />
          <AField aKey="cos" assumptions={assumptions} onChange={onChange} step="0.01" />
          <AField aKey="cus" assumptions={assumptions} onChange={onChange} step="0.01" />
        </div>
        {/* Surface validation warnings */}
        {(() => {
          const a = assumptions || {};
          const terreno = a.total_land_area_m2 || 0;
          const vendible = a.total_saleable_area_m2 || 0;
          const comunes = a.total_common_area_m2 || 0;
          const construible = a.total_buildable_area_m2 || 0;
          const cos = a.cos || 0;
          const cus = a.cus || 0;
          const warnings = [];
          if (terreno > 0 && vendible + comunes > terreno * 1.01)
            warnings.push('La suma de vendible + áreas comunes excede el terreno total');
          if (terreno > 0 && cus > 0 && construible > terreno * cus * 1.01)
            warnings.push(`Construible (${formatNumber(construible,0)} m²) excede el máximo por CUS (${formatNumber(terreno * cus,0)} m²)`);
          if (cos > 0 && cus > 0 && cos > cus)
            warnings.push('COS no puede ser mayor que CUS');
          if (warnings.length === 0) return null;
          return (
            <div className="mt-3 space-y-1.5">
              {warnings.map((w, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.15)' }}>
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--warning-color)' }} />
                  <span style={{ color: 'var(--warning-color)' }}>{w}</span>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      {/* Construction area mismatch */}
      {computedConstruction > 0 && Math.abs(computedConstruction - (assumptions?.total_buildable_area_m2 || 0)) > 1 && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm" style={{ background: 'var(--warning-soft)', border: '1px solid rgba(245,166,35,0.15)' }}>
          <Info className="h-4 w-4 shrink-0" style={{color:'var(--warning-color)'}} />
          <span style={{ color: 'var(--text-secondary)' }}>Tipologías suman <strong>{formatNumber(computedConstruction, 0)} m² de construcción</strong> ({computedUnits} uds) vs {formatNumber(assumptions?.total_buildable_area_m2 || 0, 0)} m² construibles.</span>
          <Button size="sm" variant="ghost" className="ml-auto text-xs h-7" onClick={() => { onChange('total_buildable_area_m2', computedConstruction); onChange('total_units', computedUnits); }}>
            Sincronizar
          </Button>
        </div>
      )}
      {/* Lot/saleable area mismatch */}
      {computedLot > 0 && Math.abs(computedLot - (assumptions?.total_saleable_area_m2 || 0)) > 1 && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.15)' }}>
          <Info className="h-4 w-4 shrink-0" style={{color:'var(--financial-primary)'}} />
          <span style={{ color: 'var(--text-secondary)' }}>Tipologías suman <strong>{formatNumber(computedLot, 0)} m² vendibles</strong> (terreno/lote) vs {formatNumber(assumptions?.total_saleable_area_m2 || 0, 0)} m² vendibles.</span>
          <Button size="sm" variant="ghost" className="ml-auto text-xs h-7" onClick={() => onChange('total_saleable_area_m2', computedLot)}>
            Sincronizar
          </Button>
        </div>
      )}
    </div>
  );
}

export function StepTypologies({ typologies, scenarioId, onReload, assumptions, onChange }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleUpdate = async (id, field, value) => {
    try {
      await structuredInputsAPI.typologies.update(scenarioId, id, { [field]: value });
      onReload();
    } catch { toast.error('Error al actualizar tipología'); }
  };

  const handleAdd = async () => {
    setSaving(true);
    try {
      await structuredInputsAPI.typologies.create(scenarioId, {
        name: 'Nueva Tipología', unit_type: 'departamento', quantity: 10,
        area_m2: 80, list_price: 3000000, display_order: (typologies.length + 1) * 10,
      });
      onReload();
      toast.success('Tipología agregada');
    } catch { toast.error('Error al crear tipología'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await structuredInputsAPI.typologies.delete(scenarioId, id);
      onReload();
      toast.success('Tipología eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const totalUnits = typologies.reduce((s, t) => s + (t.quantity || 0), 0);
  const totalRevenue = typologies.reduce((s, t) => s + (t.quantity || 0) * (t.list_price || 0), 0);
  const totalAbsorption = typologies.reduce((s, t) => s + (t.absorption_units_month || 0), 0);
  const globalAbsorption = assumptions?.absorption_rate_units_per_month || 0;
  const globalUnits = assumptions?.total_units || 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className="tabular-nums">{typologies.length} tipologías</Badge>
          <span className="text-sm text-muted-foreground">{totalUnits} uds  |  {formatCurrency(totalRevenue)} ingreso bruto</span>
        </div>
        <Button size="sm" variant="outline" onClick={handleAdd} disabled={saving} data-testid="add-typology-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Agregar
        </Button>
      </div>
      {totalUnits > 0 && totalUnits !== globalUnits && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-amber-200 bg-amber-50/50 text-sm">
          <Info className="h-4 w-4 shrink-0" style={{color:'var(--warning-color)'}} />
          <span className="text-muted-foreground">Tipologías suman <strong>{totalUnits} uds</strong> vs {globalUnits} en Ventas.</span>
          <Button size="sm" variant="ghost" className="ml-auto text-xs h-7" onClick={() => onChange('total_units', totalUnits)}>Sincronizar</Button>
        </div>
      )}
      {totalAbsorption > 0 && Math.abs(totalAbsorption - globalAbsorption) > 0.05 && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-blue-200 bg-blue-50/40 text-sm">
          <Info className="h-4 w-4 shrink-0" style={{color:'var(--primary-hex)'}} />
          <span className="text-muted-foreground">Absorción total tipologías: <strong>{formatNumber(totalAbsorption, 1)} uds/mes</strong> (global = {formatNumber(globalAbsorption, 1)}).</span>
          <Button size="sm" variant="ghost" className="ml-auto text-xs h-7" onClick={() => onChange('absorption_rate_units_per_month', Math.round(totalAbsorption * 10) / 10)}>Sincronizar</Button>
        </div>
      )}
      {typologies.length === 0 ? (
        <div className="text-center py-8 rounded-xl" style={{ border: '2px dashed var(--border-default)' }}>
          <Layers className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>Sin tipologías</p>
          <p className="text-xs" style={{ color: '#94A3B8' }}>Agrega una tipología para definir tu mezcla de producto</p>
        </div>
      ) : (
        <div className="space-y-3">
          {typologies.map((t) => {
            const priceM2 = t.area_m2 > 0 ? Math.round((t.list_price || 0) / t.area_m2) : 0;
            return (
              <Card key={t.id} className="rounded-2xl border-border/40 shadow-sm bg-card" data-testid={`typo-card-${t.id}`}>
                <CardContent className="p-6">
                  {/* Header: Name + Type + Delete */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 flex items-center gap-3">
                      <TableInput value={t.name} onChange={(v) => handleUpdate(t.id, 'name', v)} type="text" className="h-11 text-lg font-semibold rounded-xl flex-1" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }} />
                      <div className="shrink-0">
                        <TableInput value={t.unit_type || 'departamento'} onChange={(v) => handleUpdate(t.id, 'unit_type', v)} type="text" className="w-40 h-9 text-center text-xs font-medium rounded-full" style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' }} />
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" onClick={() => handleDelete(t.id)} data-testid={`typo-delete-${t.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Grid: top row - 3 columns */}
                  <div className="grid grid-cols-3 gap-x-8 gap-y-4 mb-4">
                    {/* Column 1: Producto */}
                    <div className="space-y-3">
                      <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Producto</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <FieldMini label="Unidades" value={t.quantity} onChange={(v) => handleUpdate(t.id, 'quantity', Math.round(v))} type="number" />
                        <FieldMini label="Absorción/mes" value={t.absorption_units_month} onChange={(v) => handleUpdate(t.id, 'absorption_units_month', v)} type="number" step="0.1" />
                      </div>
                    </div>

                    {/* Column 2: Superficies */}
                    <div className="space-y-3">
                      <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Superficies</h5>
                      <div className="grid grid-cols-2 gap-3">
                        <FieldMini label="m² construcción" value={t.area_m2} onChange={(v) => handleUpdate(t.id, 'area_m2', v)} type="number" />
                        <FieldMini label="m² terreno" value={t.terrace_m2 || 0} onChange={(v) => handleUpdate(t.id, 'terrace_m2', v)} type="number" />
                      </div>
                    </div>

                    {/* Column 3: Características */}
                    <div className="space-y-3">
                      <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Características</h5>
                      <div className="grid grid-cols-4 gap-3">
                        <FieldMini label="Niveles" value={t.floors || 0} onChange={(v) => handleUpdate(t.id, 'floors', Math.round(v))} type="number" />
                        <FieldMini label="Recámaras" value={t.bedrooms || 0} onChange={(v) => handleUpdate(t.id, 'bedrooms', Math.round(v))} type="number" />
                        <FieldMini label="Baños" value={t.bathrooms || 0} onChange={(v) => handleUpdate(t.id, 'bathrooms', v)} type="number" />
                        <FieldMini label="Estac." value={t.parking_spots || 0} onChange={(v) => handleUpdate(t.id, 'parking_spots', Math.round(v))} type="number" />
                      </div>
                    </div>
                  </div>

                  {/* Bottom row: Financiero - full width */}
                  <div className="pt-4" style={{ borderTop: '1px solid #E2E8F0' }}>
                    <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Financieros</h5>
                    <div className="grid grid-cols-3 gap-4">
                      <FieldMini label="Precio" value={t.list_price} onChange={(v) => handleUpdate(t.id, 'list_price', v)} type="currency" />
                      <FieldMini label="Costo directo/ud" value={t.direct_cost_per_unit} onChange={(v) => handleUpdate(t.id, 'direct_cost_per_unit', v)} type="currency" />
                      <div className="space-y-1.5">
                        <span className="text-xs font-medium" style={{ color: '#64748B' }}>$/m²</span>
                        <div className="h-11 flex items-center px-3 rounded-xl text-base tabular-nums font-semibold" style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0' }}>
                          {priceM2 > 0 ? `$${priceM2.toLocaleString('es-MX')}` : '—'}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Mini field for typology cards ── */
function FieldMini({ label, value, onChange, type = 'number', step }) {
  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium" style={{ color: '#64748B' }}>{label}</span>
      <TableInput value={value} onChange={onChange} type={type} className="h-11 text-base font-semibold rounded-xl" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }} />
    </div>
  );
}

export function StepLand({ assumptions, onChange, landSchedule, scenarioId, onReload }) {
  const handleAddPayment = async () => {
    try {
      await structuredInputsAPI.landSchedule.create(scenarioId, {
        concept: 'otros', description: 'Nuevo pago', amount: 0, month: 1, category: 'terreno', display_order: (landSchedule.length + 1) * 10,
      });
      onReload();
    } catch { toast.error('Error al agregar pago'); }
  };
  const handleUpdatePayment = async (id, field, value) => {
    try {
      await structuredInputsAPI.landSchedule.update(scenarioId, id, { [field]: value });
      onReload();
    } catch { toast.error('Error al actualizar'); }
  };
  const handleDeletePayment = async (id) => {
    try { await structuredInputsAPI.landSchedule.delete(scenarioId, id); onReload(); } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-4">
      {/* Terreno — card style matching Tipologías */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: '#94A3B8' }}>Terreno</h5>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
          <AField aKey="land_cost" assumptions={assumptions} onChange={onChange} />
          <AField aKey="land_payment_mode" assumptions={assumptions} onChange={onChange} options={[
            [0, 'Contado (mes 1)'],[1, 'Diferido (enganche + individualización)'],[2, 'Calendario personalizado']
          ]} />
          {assumptions?.land_payment_mode === 1 && (
            <AField aKey="land_initial_pct" assumptions={assumptions} onChange={onChange} />
          )}
        </div>
      </div>

      {/* Calendario de pagos y preoperativos — card style */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Calendario de pagos y preoperativos</h5>
          <div className="flex gap-2">
            {landSchedule.length === 0 && (
              <Button size="sm" variant="outline" onClick={async () => {
                try {
                  await structuredInputsAPI.landSchedule.loadTemplate(scenarioId);
                  onReload();
                  toast.success('Plantilla cargada — edita los montos según tu proyecto');
                } catch { toast.error('Error'); }
              }} data-testid="load-land-template-btn">
                <FileText className="h-3.5 w-3.5 mr-1.5" /> Cargar plantilla típica
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleAddPayment} data-testid="add-land-payment-btn">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Agregar
            </Button>
          </div>
        </div>
        {landSchedule.length === 0 ? (
          <div className="text-center py-8">
            <MapPin className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-muted)', opacity: 0.3 }} />
            <p className="text-sm text-muted-foreground mb-1">Sin pagos programados</p>
            <p className="text-[11px] text-muted-foreground">Agrega pagos manualmente o carga una plantilla típica con conceptos preconfigurados.</p>
          </div>
        ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="overflow-auto" style={{ maxHeight: '380px' }}>
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs min-w-[140px]">Concepto</TableHead>
                  <TableHead className="text-xs min-w-[160px]">Descripción</TableHead>
                  <TableHead className="text-xs text-right w-[140px]">Monto</TableHead>
                  <TableHead className="text-xs text-center w-[60px]">Mes</TableHead>
                  <TableHead className="text-xs text-center w-[50px]">%</TableHead>
                  <TableHead className="text-xs w-[90px]">Categoría</TableHead>
                  <TableHead className="text-xs w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {landSchedule.map((p) => (
                  <TableRow key={p.id} className="hover:bg-accent/30">
                    <TableCell>
                      <Select value={p.concept} onValueChange={(v) => handleUpdatePayment(p.id, 'concept', v)}>
                        <SelectTrigger className="h-7 text-xs border-transparent"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['anticipo','saldo','parcialidad','notariales','legales','impuestos','estudios','proyecto_arq','permisos','urbanizacion','limpieza','otros'].map(c => (
                            <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1).replace('_',' ')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TableInput value={p.description} onChange={(v) => handleUpdatePayment(p.id, 'description', v)} type="text" />
                    </TableCell>
                    <TableCell className="text-right">
                      <TableInput value={p.amount} onChange={(v) => handleUpdatePayment(p.id, 'amount', v)}
                        type={p.is_percentage ? 'pct' : 'currency'} className="w-24" />
                    </TableCell>
                    <TableCell className="text-center">
                      <TableInput value={p.month} onChange={(v) => handleUpdatePayment(p.id, 'month', Math.round(v))} type="number" className="w-12" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch checked={p.is_percentage} onCheckedChange={(v) => handleUpdatePayment(p.id, 'is_percentage', v)} className="scale-75" />
                    </TableCell>
                    <TableCell>
                      <Select value={p.category} onValueChange={(v) => handleUpdatePayment(p.id, 'category', v)}>
                        <SelectTrigger className="h-7 text-xs border-transparent"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['terreno','preoperativo','legal','estudios','permisos'].map(c => (
                            <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDeletePayment(p.id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export function StepBudget({ assumptions, onChange, budgetItems, scenarioId, onReload, structured, mode, setMode }) {
  const totalBudget = budgetItems.reduce((s, b) => s + (b.total_amount || 0), 0);
  const hardTotal = budgetItems.filter(b => b.is_hard_cost).reduce((s, b) => s + (b.total_amount || 0), 0);
  const softTotal = budgetItems.filter(b => !b.is_hard_cost).reduce((s, b) => s + (b.total_amount || 0), 0);

  // Linked: edificación + urbanización = 100%
  const handleEdifUrbChange = (key, value) => {
    onChange(key, value);
    if (key === 'hard_cost_edificacion_pct') onChange('hard_cost_urbanizacion_pct', Math.round((1.0 - value) * 10000) / 10000);
    if (key === 'hard_cost_urbanizacion_pct') onChange('hard_cost_edificacion_pct', Math.round((1.0 - value) * 10000) / 10000);
  };
  // Linked: soft cost sub-% should sum to 1.0 — auto-adjust licencias
  const handleSoftSubChange = (key, value) => {
    onChange(key, value);
    if (key === 'soft_cost_estudios_pct') {
      onChange('soft_cost_licencias_pct', Math.round(Math.max(0, 1.0 - value) * 10000) / 10000);
    }
    if (key === 'soft_cost_licencias_pct') {
      onChange('soft_cost_estudios_pct', Math.round(Math.max(0, 1.0 - value) * 10000) / 10000);
    }
  };

  const softStudies = assumptions?.soft_cost_estudios_pct || 0;
  const softPermits = assumptions?.soft_cost_licencias_pct || 0;
  const softSum = softStudies + softPermits;
  const softSumOk = Math.abs(softSum - 1.0) < 0.005;

  const handleAdd = async () => {
    try {
      await structuredInputsAPI.budgetItems.create(scenarioId, {
        category: 'otros', subcategory: 'Nueva partida', total_amount: 0, contingency_pct: 0.05, is_hard_cost: true,
        display_order: (budgetItems.length + 1) * 10,
      });
      onReload();
    } catch { toast.error('Error al agregar partida'); }
  };
  const handleUpdate = async (id, field, value) => {
    try { await structuredInputsAPI.budgetItems.update(scenarioId, id, { [field]: value }); onReload(); } catch { toast.error('Error'); }
  };
  const handleDelete = async (id) => {
    try { await structuredInputsAPI.budgetItems.delete(scenarioId, id); onReload(); } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-5">
      {/* Mode toggle */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
        <span className="text-sm text-muted-foreground">Modo de captura:</span>
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button onClick={() => { setMode('general'); onChange('budget_capture_mode', 'general'); }} data-testid="budget-mode-general"
            className={`px-4 py-1.5 text-sm font-medium transition-[background-color,color] duration-150 ${mode === 'general' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`}
            style={mode === 'general' ? { background: 'var(--primary-hex)' } : {}}>
            Costos generales
          </button>
          <button onClick={() => { setMode('detailed'); onChange('budget_capture_mode', 'detailed'); }} data-testid="budget-mode-detailed"
            className={`px-4 py-1.5 text-sm font-medium transition-[background-color,color] duration-150 ${mode === 'detailed' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`}
            style={mode === 'detailed' ? { background: 'var(--primary-hex)' } : {}}>
            Partidas detalladas
          </button>
        </div>
        {mode === 'detailed' && budgetItems.length > 0 && (
          <span className="text-xs text-muted-foreground ml-auto tabular-nums">
            Duros: {formatCurrency(hardTotal)} | Blandos: {formatCurrency(softTotal)} | Total: {formatCurrency(totalBudget)}
          </span>
        )}
      </div>

      {/* Config card */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Configuración</h5>
        <div className="grid grid-cols-3 gap-x-6 gap-y-3">
          <AField aKey="construction_curve" assumptions={assumptions} onChange={onChange} options={[
            [0,'Curva S'],[1,'Lineal'],[2,'Acelerada'],[3,'Diferida'],[4,'Trapezoidal'],[5,'Pico temprano']
          ]} />
          <AField aKey="construction_months" assumptions={assumptions} onChange={onChange} />
          <AField aKey="contingency_pct" assumptions={assumptions} onChange={onChange} />
          {/* SWA-217 · urbanización: el motor la usa con presupuesto general y detallado */}
          <AField aKey="hard_cost_urbanizacion_amount" assumptions={assumptions} onChange={onChange} />
          <AField aKey="urbanization_curve" assumptions={assumptions} onChange={onChange} options={[[0,'Frontal (al inicio de la obra)'],[1,'Misma curva que edificación']]} />
          {Number(assumptions?.urbanization_curve || 0) === 0 && (
            <AField aKey="urbanization_months_pct" assumptions={assumptions} onChange={onChange} />
          )}
        </div>
      </div>

      {mode === 'general' ? (
        <>
          {/* Costos Duros */}
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Costos duros</h5>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <div className="space-y-1.5">
                <AField aKey="hard_cost_per_unit" assumptions={assumptions} onChange={onChange} />
                <TypologyCostHint assumptions={assumptions} structured={structured} onChange={onChange} />
              </div>
            <AField aKey="hard_cost_total_override" assumptions={assumptions} onChange={onChange} />
            <AField aKey="hard_cost_per_m2" assumptions={assumptions} onChange={onChange} info />
            <AField aKey="hard_cost_edificacion_pct" assumptions={assumptions} onChange={handleEdifUrbChange} />
            <AField aKey="hard_cost_urbanizacion_pct" assumptions={assumptions} onChange={handleEdifUrbChange} />
            </div>
          </div>

          {/* Costos Blandos de Desarrollo */}
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>Costos blandos de desarrollo</h5>
            <p className="text-[10px] mb-3" style={{ color: '#94A3B8' }}>Estudios, proyectos, licencias y permisos. La operación corriente del proyecto se captura en Gastos Indirectos (Paso 5).</p>
            <div className="grid grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="soft_cost_pct" assumptions={assumptions} onChange={onChange} />
              <AField aKey="soft_cost_estudios_pct" assumptions={assumptions} onChange={handleSoftSubChange} info />
              <AField aKey="soft_cost_licencias_pct" assumptions={assumptions} onChange={handleSoftSubChange} info />
            </div>
            {/* Validation banner */}
            <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: softSumOk ? 'rgba(12,191,115,0.08)' : 'rgba(245,166,35,0.08)', border: `1px solid ${softSumOk ? 'rgba(12,191,115,0.2)' : 'rgba(245,166,35,0.2)'}` }}>
              {softSumOk
                ? <><CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--success)' }} /> <span style={{ color: 'var(--success)' }}>Estudios + Licencias suman 100%</span></>
                : <><AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--warning-color)' }} /> <span style={{ color: 'var(--warning-color)' }}>Estudios + Licencias deben sumar 100% (actual: {Math.round(softSum * 100)}%)</span></>
              }
            </div>
          </div>

          {/* Otros */}
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Otros costos</h5>
            <div className="grid grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="development_fee" assumptions={assumptions} onChange={onChange} />
              <AField aKey="construction_inflation_pct" assumptions={assumptions} onChange={onChange} />
              <AField aKey="expected_overrun_pct" assumptions={assumptions} onChange={onChange} />
            </div>
          </div>
        </>
      ) : (
        <>
          <Separator />
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-display font-semibold text-muted-foreground uppercase tracking-wide">Partidas de Presupuesto</h4>
            <Button size="sm" variant="outline" onClick={handleAdd} data-testid="add-budget-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Agregar</Button>
          </div>
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-auto" style={{ maxHeight: '420px' }}>
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs min-w-[130px]">Categoría</TableHead>
                    <TableHead className="text-xs min-w-[140px]">Subcategoría</TableHead>
                    <TableHead className="text-xs text-right w-[130px]">Monto Total</TableHead>
                    <TableHead className="text-xs text-right w-[80px]">Conting. %</TableHead>
                    <TableHead className="text-xs text-center w-[70px]">Cto Duro</TableHead>
                    <TableHead className="text-xs w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgetItems.map((b) => (
                    <TableRow key={b.id} className="hover:bg-accent/30">
                      <TableCell>
                        <Select value={b.category} onValueChange={(v) => handleUpdate(b.id, 'category', v)}>
                          <SelectTrigger className="h-7 text-xs border-transparent"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['cimentacion','estructura','albañileria','instalacion_hidraulica','instalacion_electrica','instalacion_gas','acabados_interiores','acabados_exteriores','carpinteria','herreria','canceleria','pintura','impermeabilizacion','urbanizacion','areas_comunes','equipamiento','otros'].map(c => (
                              <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1).replace(/_/g,' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TableInput value={b.subcategory} onChange={(v) => handleUpdate(b.id, 'subcategory', v)} type="text" />
                      </TableCell>
                      <TableCell className="text-right">
                        <TableInput value={b.total_amount} onChange={(v) => handleUpdate(b.id, 'total_amount', v)} type="currency" className="w-28" />
                      </TableCell>
                      <TableCell className="text-right">
                        <TableInput value={Math.round((b.contingency_pct || 0) * 10000) / 100}
                          onChange={(v) => handleUpdate(b.id, 'contingency_pct', v / 100)} type="pct" className="w-16" />
                      </TableCell>
                      <TableCell className="text-center">
                        <Switch checked={b.is_hard_cost} onCheckedChange={(v) => handleUpdate(b.id, 'is_hard_cost', v)} className="scale-75" />
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(b.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-4 pt-2">
            <AField aKey="development_fee" assumptions={assumptions} onChange={onChange} />
            <AField aKey="construction_inflation_pct" assumptions={assumptions} onChange={onChange} />
            <AField aKey="expected_overrun_pct" assumptions={assumptions} onChange={onChange} />
          </div>
        </>
      )}
    </div>
  );
}

export function StepIndirect({ assumptions, onChange, indirectCosts, scenarioId, onReload, mode, setMode }) {

  const handleAdd = async () => {
    try {
      await structuredInputsAPI.indirectCosts.create(scenarioId, {
        category: 'otros', description: 'Nuevo gasto', cost_type: 'fixed_monthly', amount: 0, phase: 'todas',
        display_order: (indirectCosts.length + 1) * 10,
      });
      onReload();
    } catch { toast.error('Error'); }
  };
  const handleUpdate = async (id, field, value) => {
    try { await structuredInputsAPI.indirectCosts.update(scenarioId, id, { [field]: value }); onReload(); } catch { toast.error('Error'); }
  };
  const handleDelete = async (id) => {
    try { await structuredInputsAPI.indirectCosts.delete(scenarioId, id); onReload(); } catch { toast.error('Error'); }
  };

  const COST_TYPE_LABELS = { fixed_monthly: 'Fijo mensual', fixed_total: 'Fijo total', percentage_revenue: '% ingreso', percentage_cost: '% costo', per_unit_sold: 'Por ud vendida', per_unit_delivered: 'Por ud entregada', one_time: 'Único' };

  return (
    <div className="space-y-5">
      {/* Mode toggle */}
      <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
        <span className="text-sm text-muted-foreground">Modo de captura:</span>
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button onClick={() => { setMode('general'); onChange('indirect_capture_mode', 'general'); }} data-testid="indirect-mode-general"
            className={`px-4 py-1.5 text-sm font-medium transition-[background-color,color] duration-150 ${mode === 'general' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`}
            style={mode === 'general' ? { background: 'var(--primary-hex)' } : {}}>
            Porcentajes generales
          </button>
          <button onClick={() => { setMode('detailed'); onChange('indirect_capture_mode', 'detailed'); }} data-testid="indirect-mode-detailed"
            className={`px-4 py-1.5 text-sm font-medium transition-[background-color,color] duration-150 ${mode === 'detailed' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`}
            style={mode === 'detailed' ? { background: 'var(--primary-hex)' } : {}}>
            Partidas detalladas
          </button>
        </div>
      </div>

      {mode === 'general' ? (
        <div className="space-y-4">
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Gastos operativos</h5>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="admin_monthly" assumptions={assumptions} onChange={onChange} />
              <AField aKey="supervision_monthly" assumptions={assumptions} onChange={onChange} />
              <AField aKey="marketing_budget_pct" assumptions={assumptions} onChange={onChange} />
              <AField aKey="sales_commission_pct" assumptions={assumptions} onChange={onChange} />
              <AField aKey="insurance_pct" assumptions={assumptions} onChange={onChange} />
              <AField aKey="post_sale_per_unit" assumptions={assumptions} onChange={onChange} />
            </div>
          </div>
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Overrides</h5>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <AField aKey="indirect_cost_total_override" assumptions={assumptions} onChange={onChange} />
              <AField aKey="indirect_cost_pct_revenue" assumptions={assumptions} onChange={onChange} info />
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Badge variant="secondary">{indirectCosts.length} partidas</Badge>
            <Button size="sm" variant="outline" onClick={handleAdd} data-testid="add-indirect-btn"><Plus className="h-3.5 w-3.5 mr-1.5" /> Agregar</Button>
          </div>
          <div className="rounded-xl border border-border overflow-hidden">
            <div className="overflow-auto" style={{ maxHeight: '420px' }}>
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow className="bg-muted/30">
                    <TableHead className="text-xs min-w-[130px]">Categoría</TableHead>
                    <TableHead className="text-xs min-w-[150px]">Descripción</TableHead>
                    <TableHead className="text-xs w-[120px]">Tipo Costo</TableHead>
                    <TableHead className="text-xs text-right w-[110px]">Monto</TableHead>
                    <TableHead className="text-xs w-[90px]">Fase</TableHead>
                    <TableHead className="text-xs w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {indirectCosts.map((ic) => (
                    <TableRow key={ic.id} className="hover:bg-accent/30">
                      <TableCell>
                        <Select value={ic.category} onValueChange={(v) => handleUpdate(ic.id, 'category', v)}>
                          <SelectTrigger className="h-7 text-xs border-transparent"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['administracion','oficina_obra','nomina_admin','seguridad','supervision','gerencia_proyecto','honorarios_tecnicos','asesoria_legal','asesoria_financiera','marketing','ventas','comisiones_ventas','postventa','servicios','seguros','fianzas','otros'].map(c => (
                              <SelectItem key={c} value={c}>{c.replace(/_/g,' ').replace(/^\w/,l=>l.toUpperCase())}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TableInput value={ic.description} onChange={(v) => handleUpdate(ic.id, 'description', v)} type="text" />
                      </TableCell>
                      <TableCell>
                        <Select value={ic.cost_type} onValueChange={(v) => handleUpdate(ic.id, 'cost_type', v)}>
                          <SelectTrigger className="h-7 text-xs border-transparent"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(COST_TYPE_LABELS).map(([k,v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <TableInput value={ic.amount} onChange={(v) => handleUpdate(ic.id, 'amount', v)}
                          type={ic.cost_type?.includes('percentage') ? 'pct' : 'currency'} className="w-24" />
                      </TableCell>
                      <TableCell>
                        <Select value={ic.phase || 'todas'} onValueChange={(v) => handleUpdate(ic.id, 'phase', v)}>
                          <SelectTrigger className="h-7 text-xs border-transparent"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[['todas','Todas'],['pre_desarrollo','Pre-desarrollo'],['preventa','Preventa'],['construccion','Construcción'],['entrega','Entrega'],['postventa','Postventa']].map(([k,v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(ic.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export function StepSchedule({ assumptions, onChange, mode, setMode }) {
  const [localUrb, setLocalUrb] = useState(null);
  const [localEdif, setLocalEdif] = useState(null);

  const handleLinkedChange = (key, value) => {
    onChange(key, value);
    const a = { ...assumptions, [key]: value };
    if (key === 'construction_start_month' || key === 'construction_months') {
      const start = key === 'construction_start_month' ? value : (a.construction_start_month || 7);
      const months = key === 'construction_months' ? value : (a.construction_months || 24);
      onChange('construction_end_month', start + months - 1);
    }
    if (key === 'construction_end_month') {
      const start = a.construction_start_month || 7;
      if (value > start) onChange('construction_months', value - start + 1);
    }
  };

  const presale = assumptions?.presale_months || 0;
  const constrMonths = assumptions?.construction_months || 24;
  const postMonths = assumptions?.post_construction_months || 0;
  const totalMonths = presale + constrMonths + postMonths;

  // SWA-157 · el horizonte no es insumo: es n = len(monthly_schedule) de la corrida.
  // Se lee de la última corrida de este escenario; sin corrida, solo se conoce el piso.
  const runHorizon = useRunHorizon();

  // Parse stored JSON only once when assumptions change
  useEffect(() => {
    try {
      const raw = assumptions?.custom_curve_urb_json;
      if (typeof raw === 'string' && raw.startsWith('[') && !localUrb) setLocalUrb(JSON.parse(raw));
    } catch (err) { console.warn('[ScenarioWizard] parse custom_curve_urb_json failed', err); }
    try {
      const raw = assumptions?.custom_curve_edif_json;
      if (typeof raw === 'string' && raw.startsWith('[') && !localEdif) setLocalEdif(JSON.parse(raw));
    } catch (err) { console.warn('[ScenarioWizard] parse custom_curve_edif_json failed', err); }
  }, []);

  // SWA-222 · el toggle se inicializa desde la key guardada, no desde el dato.
  // La precedencia replica exactamente la del motor para este eje.
  const modeInitRef = useRef(false);
  useEffect(() => {
    if (modeInitRef.current) return;
    if (!assumptions || Object.keys(assumptions).length === 0) return;
    modeInitRef.current = true;
    const raw = assumptions.construction_curve_mode;
    // El motor (curves.resolve_curve_source, curves.py:77) lee este modo con
    // default 0 y NO tiene fallback por presencia de las curvas: sin key corre
    // la curva automática. La interfaz replica esa precedencia exacta.
    const effective = Number(raw || 0) === 1 ? 'detailed' : 'general';
    if (effective === 'detailed' && mode !== 'detailed') setMode('detailed');
    if (effective === 'general' && mode === 'detailed') setMode('general');
  }, [assumptions]);

  // SWA-152 · la curva la calcula el motor. Nunca se reimplementa aquí:
  // una copia en JS ya divergió del catálogo y pintaba curva S para los seis tipos.
  const curveType = assumptions?.construction_curve ?? 0;
  const [autoWeights, setAutoWeights] = useState(null);
  const [curveLoading, setCurveLoading] = useState(false);
  const [curveError, setCurveError] = useState(null);

  useEffect(() => {
    if (!(constrMonths > 0)) { setAutoWeights([]); setCurveError(null); return; }
    let cancelled = false;
    setCurveLoading(true);
    setAutoWeights(null);
    setCurveError(null);
    const timer = setTimeout(async () => {
      try {
        const res = await analyzerAPI.curvePreview(Number(curveType), Number(constrMonths));
        if (cancelled) return;
        setAutoWeights((res.data.weights || []).map(w => w / 100));
      } catch (err) {
        if (cancelled) return;
        setCurveError(err?.response?.data?.detail || 'No se pudo obtener la previsualización de la curva');
        setAutoWeights(null);
      } finally {
        if (!cancelled) setCurveLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [curveType, constrMonths]);

  const schedulePreview = useMemo(() => {
    if (constrMonths <= 0 || totalMonths <= 0) return [];
    if (mode !== 'detailed' && !autoWeights) return [];
    const rows = []; let cumUrb = 0, cumEdif = 0;
    for (let m = 0; m < totalMonths; m++) {
      const ci = m - presale; let uMo = 0, eMo = 0;
      if (ci >= 0 && ci < constrMonths) {
        if (mode === 'detailed' && localUrb && localEdif) {
          uMo = (localUrb[ci] || 0) / 100; eMo = (localEdif[ci] || 0) / 100;
        } else {
          // SWA-212 · cada serie es el avance de SU partida: la curva completa,
          // no la fracción del split. El motor aplica cada curva a su subtotal
          // (engine/costs.py:129), así que cada una cierra en 100 %.
          const w = autoWeights[ci] || 0;
          uMo = w; eMo = w;
        }
      }
      cumUrb += uMo; cumEdif += eMo;
      rows.push({ month: m + 1, ci, phase: m < presale ? 'preventa' : m < presale + constrMonths ? 'construccion' : 'postventa', urbMo: Math.round(uMo * 1000) / 10, urbAcum: Math.round(cumUrb * 1000) / 10, edifMo: Math.round(eMo * 1000) / 10, edifAcum: Math.round(cumEdif * 1000) / 10 });
    }
    return rows;
  }, [presale, constrMonths, totalMonths, mode, localUrb, localEdif, autoWeights]);

  /* SWA-212 · siembra de una serie: la curva del motor normalizada a 100.0 %
   * exactos con un decimal. El residuo del redondeo se carga al mes de mayor
   * peso, no al último: en una curva diferida el último mes puede ser casi
   * cero y 0.x pp ahí deforma la cola del flujo. */
  const seedSeries = (weights) => {
    const arr = (weights || []).map(w => Math.round((w || 0) * 1000) / 10);
    const sum = arr.reduce((s, v) => s + v, 0);
    const residual = Math.round((100 - sum) * 10) / 10;
    if (residual !== 0 && arr.length) {
      let peak = 0;
      for (let i = 1; i < arr.length; i++) if (arr[i] > arr[peak]) peak = i;
      arr[peak] = Math.round((arr[peak] + residual) * 10) / 10;
    }
    return arr;
  };

  const handleManualEdit = (ci, field, pctVal) => {
    const isUrb = field === 'urb';
    const defaultArr = seedSeries(autoWeights);
    if (isUrb) {
      const updated = [...(localUrb || defaultArr)]; while (updated.length <= ci) updated.push(0);
      updated[ci] = pctVal;
      setLocalUrb(updated);
      onChange('custom_curve_urb_json', JSON.stringify(updated));
    } else {
      const updated = [...(localEdif || defaultArr)]; while (updated.length <= ci) updated.push(0);
      updated[ci] = pctVal;
      setLocalEdif(updated);
      onChange('custom_curve_edif_json', JSON.stringify(updated));
    }
  };

  const handleSwitchToManual = () => {
    // SWA-152 · siembra desde la serie del motor. Si no llegó, no se siembra nada.
    if (!autoWeights || autoWeights.length === 0) {
      toast.error('No se pudo obtener la curva del motor; no se sembraron valores manuales');
      return;
    }
    const urbArr = seedSeries(autoWeights);
    const edifArr = seedSeries(autoWeights);
    if (!localUrb) { setLocalUrb(urbArr); onChange('custom_curve_urb_json', JSON.stringify(urbArr)); }
    if (!localEdif) { setLocalEdif(edifArr); onChange('custom_curve_edif_json', JSON.stringify(edifArr)); }
    onChange('construction_curve_mode', 1);
    setMode('detailed');
  };

  const lastRow = schedulePreview[schedulePreview.length - 1];
  const totalUrb = lastRow?.urbAcum || 0;
  const totalEdif = lastRow?.edifAcum || 0;
  // SWA-212 · el exceso se mide POR SERIE contra su propio 100 %, no sumando ambas.
  const urbOver = totalUrb > 100.5;
  const edifOver = totalEdif > 100.5;
  const overLimit = urbOver || edifOver;
  // SWA-216 · aviso de faltante, por SERIE (cada curva cierra en 100 % de SU
  // partida, SWA-212). Tolerancia de 0.5 pp, simétrica al umbral de exceso.
  const urbUnder = mode === 'detailed' && totalUrb > 0 && totalUrb < 99.5;
  const edifUnder = mode === 'detailed' && totalEdif > 0 && totalEdif < 99.5;
  const underLimit = urbUnder || edifUnder;
  const phaseColors = { preventa: '#635BFF', construccion: '#F5A623', postventa: '#697386' };

  return (
    <div className="space-y-5">
      {/* Duration row */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <div className="grid grid-cols-4 gap-4 items-end">
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Preventa</Label>
            <AField aKey="presale_months" assumptions={assumptions} onChange={handleLinkedChange} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Construcción</Label>
            <div className="h-9 flex items-center px-3 rounded-md text-sm tabular-nums" style={{ background: '#EEF2FF', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}>
              {constrMonths} meses
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Post-construcción</Label>
            <AField aKey="post_construction_months" assumptions={assumptions} onChange={handleLinkedChange} />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--accent-primary)' }}>Horizonte total</Label>
            <div className="min-h-9 flex items-center px-3 py-1.5 rounded-md text-sm font-semibold tabular-nums" style={{ background: 'var(--accent-subtle)', border: '1px solid rgba(99,91,255,0.15)', color: 'var(--accent-primary)' }} data-testid="schedule-horizon-readout">
              {runHorizon
                ? `Horizonte: ${runHorizon} meses`
                : `Mínimo ${totalMonths} meses; se extiende si la cobranza lo pide`}
            </div>
          </div>
        </div>
      </div>

      {/* SWA-196 · «Fechas clave» oculto (firmado 2-nov): el motor no lee estas fechas y contradecían
          el calendario que sí calcula (preventa + construcción + post). Lo guardado se conserva.
          Ver «Registro de campos ocultos y diferidos». */}

      {/* Avance de obra */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <div className="flex items-center gap-3 mb-3">
          <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Avance de obra</h5>
          <div className="flex rounded-lg border border-border overflow-hidden ml-auto">
            <button onClick={() => { setMode('general'); onChange('construction_curve_mode', 0); /* SWA-215 · conmutar no borra las curvas capturadas */ }} className={`px-3 py-1 text-[10px] font-medium transition-all ${mode !== 'detailed' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`} style={mode !== 'detailed' ? { background: 'var(--primary-hex)' } : {}} data-testid="schedule-mode-general">Parámetros generales</button>
            <button onClick={handleSwitchToManual} className={`px-3 py-1 text-[10px] font-medium transition-all ${mode === 'detailed' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`} style={mode === 'detailed' ? { background: 'var(--primary-hex)' } : {}} data-testid="schedule-mode-detailed">Captura mensual</button>
          </div>
          {totalMonths > 0 && (
            <span className="text-[10px] tabular-nums" style={{ color: '#94A3B8' }} data-testid="curve-progress-label">
              Urbanización: <span style={urbOver ? { color: 'var(--danger)' } : {}}>{totalUrb}%</span> de 100%
              {'  |  '}
              Edificación: <span style={edifOver ? { color: 'var(--danger)' } : {}}>{totalEdif}%</span> de 100%
            </span>
          )}
        </div>
      {mode !== 'detailed' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          <AField aKey="construction_curve" assumptions={assumptions} onChange={() => {}} disabled options={[[0,'Curva S'],[1,'Lineal'],[2,'Acelerada'],[3,'Diferida'],[4,'Trapezoidal'],[5,'Pico temprano']]} />
        </div>
      )}
      {overLimit && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-red-200 bg-red-50/50 text-sm" data-testid="curve-over-limit-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" style={{color:'var(--danger)'}} />
          <span style={{color:'var(--danger)'}}>
            {urbOver && edifOver
              ? `Urbanización (${totalUrb}%) y edificación (${totalEdif}%) superan el 100% de su partida.`
              : urbOver
                ? `El avance de urbanización (${totalUrb}%) supera el 100% de su partida.`
                : `El avance de edificación (${totalEdif}%) supera el 100% de su partida.`}
            {' '}Cada serie se captura contra su propio presupuesto. Revisa los porcentajes mensuales.
          </span>
        </div>
      )}
      {underLimit && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm" style={{ background: 'var(--warning-soft)', border: '1px solid rgba(245,166,35,0.15)' }} data-testid="curve-under-limit-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: 'var(--warning-color)' }} />
          <span style={{ color: 'var(--warning-color)' }}>
            {urbUnder && edifUnder
              ? `Urbanización (${totalUrb}%) y edificación (${totalEdif}%) no alcanzan el 100% de su partida.`
              : urbUnder
                ? `El avance de urbanización (${totalUrb}%) no alcanza el 100% de su partida.`
                : `El avance de edificación (${totalEdif}%) no alcanza el 100% de su partida.`}
            {' '}Cada serie se captura contra el 100% del presupuesto de SU partida. El análisis corre con lo capturado, sin completar el faltante.
          </span>
        </div>
      )}
      <div className="flex rounded-lg overflow-hidden h-7 border border-border/50">
        {presale > 0 && <div className="flex items-center justify-center text-[10px] font-medium text-white" style={{ width: `${(presale/totalMonths)*100}%`, background: phaseColors.preventa }}>Pre ({presale}m)</div>}
        <div className="flex items-center justify-center text-[10px] font-medium text-white" style={{ width: `${(constrMonths/totalMonths)*100}%`, background: phaseColors.construccion }}>Obra ({constrMonths}m)</div>
        {postMonths > 0 && <div className="flex items-center justify-center text-[10px] font-medium text-white" style={{ width: `${(postMonths/totalMonths)*100}%`, background: phaseColors.postventa }}>Post ({postMonths}m)</div>}
      </div>
      {/* SWA-152 · estado de la previsualización servida por el motor */}
      {curveLoading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1" data-testid="curve-preview-loading">
          <Skeleton className="h-4 w-4 rounded-full" />
          Calculando la curva en el motor…
        </div>
      )}
      {curveError && (
        <div className="rounded-xl border px-3 py-2 text-xs" style={{ background: '#FDF2F2', borderColor: '#E5AAAA', color: '#DC2626' }} data-testid="curve-preview-error">
          {curveError}. La tabla no muestra ninguna curva: no se calcula localmente para no divergir del motor.
        </div>
      )}

      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: '340px' }}>
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow className="bg-muted/30 text-[11px]">
                <TableHead className="text-center w-[45px]">Mes</TableHead>
                <TableHead className="text-center w-[50px]">Fase</TableHead>
                <TableHead className="text-right w-[75px]">Urb. %</TableHead>
                <TableHead className="text-right w-[85px]">Urb. acum<span className="text-[9px] font-normal text-muted-foreground"> /100%</span></TableHead>
                <TableHead className="text-right w-[75px]">Edif. %</TableHead>
                <TableHead className="text-right w-[85px]">Edif. acum<span className="text-[9px] font-normal text-muted-foreground"> /100%</span></TableHead>
                <TableHead className="w-[100px]">Avance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schedulePreview.map((r) => {
                const canEdit = mode === 'detailed' && r.phase === 'construccion';
                return (
                  <TableRow key={r.month} className="text-xs hover:bg-accent/30">
                    <TableCell className="text-center tabular-nums font-medium">{r.month}</TableCell>
                    <TableCell className="text-center"><span className="text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium" style={{ background: phaseColors[r.phase] }}>{r.phase === 'preventa' ? 'Pre' : r.phase === 'construccion' ? 'Obra' : 'Post'}</span></TableCell>
                    <TableCell className="text-right tabular-nums">{canEdit ? <TableInput value={r.urbMo} onChange={(v) => handleManualEdit(r.ci, 'urb', v)} type="pctpts" className="w-14" testId={`curve-urb-mo-${r.month}`} /> : r.urbMo > 0 ? `${r.urbMo}%` : '\u2014'}</TableCell>
                    <TableCell className="text-right tabular-nums" style={r.urbAcum > 100.5 ? {color:'var(--danger)'} : {}}>{r.urbAcum > 0 ? `${r.urbAcum}%` : '\u2014'}</TableCell>
                    <TableCell className="text-right tabular-nums">{canEdit ? <TableInput value={r.edifMo} onChange={(v) => handleManualEdit(r.ci, 'edif', v)} type="pctpts" className="w-14" testId={`curve-edif-mo-${r.month}`} /> : r.edifMo > 0 ? `${r.edifMo}%` : '\u2014'}</TableCell>
                    <TableCell className="text-right tabular-nums" style={r.edifAcum > 100.5 ? {color:'var(--danger)'} : {}}>{r.edifAcum > 0 ? `${r.edifAcum}%` : '\u2014'}</TableCell>
                    <TableCell><div className="w-full h-3 bg-muted/30 rounded-full overflow-hidden flex"><div className="h-full" style={{ width: `${Math.min(r.urbAcum,100)/2}%`, background: '#635BFF' }} /><div className="h-full" style={{ width: `${Math.min(r.edifAcum,100)/2}%`, background: '#F5A623' }} /></div></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
      </div>
    </div>
  );
}

export function StepSales({ assumptions, onChange, structured, mode, setMode, marketSuggestions = {} }) {
  const [localSales, setLocalSales] = useState(null);
  const typos = structured?.typologies || [];
  const computedUnits = typos.reduce((s, t) => s + (t.quantity || 0), 0);
  const totalUnits = assumptions?.total_units || 80;
  const presale = assumptions?.presale_months || 0;
  const constrMonths = assumptions?.construction_months || 24;
  const postMonths = assumptions?.post_construction_months || 0;
  const totalMonths = presale + constrMonths + postMonths;
  const absorption = assumptions?.absorption_rate_units_per_month || 3;
  const constrFactor = assumptions?.construction_sales_factor || 0.3;
  const presalePct = assumptions?.presale_pct || 0.30;
  const presaleUnits = Math.round(totalUnits * presalePct);

  // Load stored manual sales once
  useEffect(() => {
    try {
      const raw = assumptions?.custom_sales_monthly_json;
      if (typeof raw === 'string' && raw.startsWith('[') && !localSales) setLocalSales(JSON.parse(raw));
    } catch (err) { console.warn('[ScenarioWizard] parse custom_sales_monthly_json failed', err); }
  }, []);

  // SWA-222 · el toggle se inicializa desde `sales_capture_mode`, no desde la
  // presencia del JSON. Misma precedencia que revenue.resolve_sales_mode.
  const modeInitRef = useRef(false);
  useEffect(() => {
    if (modeInitRef.current) return;
    if (!assumptions || Object.keys(assumptions).length === 0) return;
    modeInitRef.current = true;
    const key = String(assumptions.sales_capture_mode || '').trim().toLowerCase();
    let hasJson = false;
    try {
      const v = JSON.parse(assumptions.custom_sales_monthly_json || '');
      hasJson = Array.isArray(v) && v.length > 0;
    } catch { hasJson = false; }
    const effective = key ? (key === 'manual' ? 'detailed' : 'general') : (hasJson ? 'detailed' : 'general');
    if (effective === 'detailed' && mode !== 'detailed') setMode('detailed');
    if (effective === 'general' && mode === 'detailed') setMode('general');
  }, [assumptions]);

  // Auto sales computation
  const autoSales = useMemo(() => {
    if (totalMonths <= 0) return [];
    const arr = []; let cum = 0;
    for (let m = 0; m < totalMonths; m++) {
      let u = 0;
      if (m < presale && presale > 0) u = presaleUnits / presale;
      else if (m < presale + constrMonths) u = Math.min(absorption * constrFactor, totalUnits - cum);
      else u = Math.min(absorption, totalUnits - cum);
      u = Math.max(0, Math.min(u, totalUnits - cum));
      cum += u;
      arr.push(Math.round(u * 100) / 100);
    }
    return arr;
  }, [totalMonths, presale, constrMonths, presaleUnits, absorption, constrFactor, totalUnits]);

  const salesPreview = useMemo(() => {
    if (totalMonths <= 0) return [];
    const source = mode === 'detailed' && localSales ? localSales : autoSales;
    const rows = []; let cum = 0;
    for (let m = 0; m < totalMonths; m++) {
      const u = source[m] || 0;
      cum += u;
      rows.push({ month: m + 1, phase: m < presale ? 'preventa' : m < presale + constrMonths ? 'construccion' : 'postventa', units: Math.round(u * 100) / 100, cumSold: Math.round(cum * 100) / 100, pctSold: Math.round(cum / Math.max(1, totalUnits) * 1000) / 10 });
    }
    return rows;
  }, [totalMonths, presale, constrMonths, totalUnits, mode, localSales, autoSales]);

  const handleManualSalesEdit = (idx, value) => {
    const current = localSales || [...autoSales];
    const updated = [...current]; while (updated.length <= idx) updated.push(0);
    updated[idx] = value;
    setLocalSales(updated);
    onChange('custom_sales_monthly_json', JSON.stringify(updated));
  };

  const handleSwitchToManual = () => {
    if (!localSales) {
      setLocalSales([...autoSales]);
      onChange('custom_sales_monthly_json', JSON.stringify(autoSales));
    }
    onChange('sales_capture_mode', 'manual');
    setMode('detailed');
  };

  const handleLinkedChange = (key, value) => {
    onChange(key, value);
    const a = { ...assumptions, [key]: value };
    if (key === 'first_delivery_pct' && (a.construction_months || 0) > 0)
      onChange('first_delivery_month', Math.max(0, Math.round((a.presale_months || 0) + (a.construction_months || 24) * value)));
    if (key === 'first_delivery_month' && value > 0 && (a.construction_months || 0) > 0)
      onChange('first_delivery_pct', Math.round(Math.max(0, (value - (a.presale_months || 0)) / (a.construction_months || 24)) * 100) / 100);
  };

  const lastRow = salesPreview[salesPreview.length - 1];
  const cumFinal = lastRow?.cumSold || 0;
  const overLimit = cumFinal > totalUnits + 0.5;
  // SWA-216 · aviso de faltante en UNIDADES del proyecto. Tolerancia: media
  // unidad o 0.5 % del total, la mayor de las dos.
  const salesTol = Math.max(0.5, totalUnits * 0.005);
  const underLimit = mode === 'detailed' && totalUnits > 0 && cumFinal > 0 && cumFinal < totalUnits - salesTol;
  const phaseColors = { preventa: '#635BFF', construccion: '#F5A623', postventa: '#697386' };

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <div className="flex items-center gap-3">
          <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Programa de ventas</h5>
          <div className="flex rounded-lg border border-border overflow-hidden ml-auto">
            <button onClick={() => { setMode('general'); onChange('sales_capture_mode', 'general'); /* SWA-215 · conmutar no borra el calendario capturado */ }} className={`px-3 py-1 text-[10px] font-medium transition-all ${mode !== 'detailed' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`} style={mode !== 'detailed' ? { background: 'var(--primary-hex)' } : {}} data-testid="sales-mode-general">Parámetros generales</button>
            <button onClick={handleSwitchToManual} className={`px-3 py-1 text-[10px] font-medium transition-all ${mode === 'detailed' ? 'text-white' : 'text-muted-foreground hover:bg-muted/50'}`} style={mode === 'detailed' ? { background: 'var(--primary-hex)' } : {}} data-testid="sales-mode-detailed">Captura mensual</button>
          </div>
          <span className="text-[10px] tabular-nums" style={{ color: '#94A3B8' }}>{Math.round(cumFinal)} / {totalUnits} uds</span>
        </div>
      </div>

      {mode !== 'detailed' && (<>
        {typos.length > 0 && computedUnits !== totalUnits && (
          <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm" style={{ background: 'var(--warning-soft)', border: '1px solid rgba(245,166,35,0.15)' }}>
            <Info className="h-4 w-4 shrink-0" style={{color:'var(--warning-color)'}} />
            <span style={{ color: 'var(--text-secondary)' }}>Tipologías suman <strong>{computedUnits} uds</strong> (total = {totalUnits}).</span>
            <Button size="sm" variant="ghost" className="ml-auto text-xs h-7" onClick={() => onChange('total_units', computedUnits)}>Sincronizar</Button>
          </div>
        )}
        <div className="rounded-2xl border border-border/40 bg-card p-6">
          <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Parámetros comerciales</h5>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            <AField aKey="total_units" assumptions={assumptions} onChange={onChange} />
            <AField aKey="presale_pct" assumptions={assumptions} onChange={onChange} />
            <div className="space-y-1.5">
              <AField aKey="absorption_rate_units_per_month" assumptions={assumptions} onChange={onChange} step="0.1" />
              <TypologyAbsorptionHint assumptions={assumptions} structured={structured} onChange={onChange} />
              <MarketHint suggestion={marketSuggestions.absorption_rate_units_per_month} onApply={(v) => onChange('absorption_rate_units_per_month', v)} />
            </div>
            <AField aKey="construction_sales_factor" assumptions={assumptions} onChange={onChange} step="0.01" />
            <AField aKey="cancellation_rate_pct" assumptions={assumptions} onChange={onChange} />
          </div>
        </div>
      </>)}

      {/* SWA-217 · campos que el motor usa en AMBOS modos de ventas: fuera del selector */}
      <div className="rounded-2xl border border-border/40 bg-card p-6" data-testid="ventas-comun">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Precio · aplica en ambos modos</h5>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
          <div className="space-y-1.5">
            <AField aKey="avg_price_per_unit" assumptions={assumptions} onChange={onChange} />
            <TypologyPriceHint assumptions={assumptions} structured={structured} onChange={onChange} />
            <MarketHint suggestion={marketSuggestions.avg_price_per_unit} onApply={(v) => onChange('avg_price_per_unit', v)} />
          </div>
          <AField aKey="price_escalation_pct" assumptions={assumptions} onChange={onChange} />
          <AField aKey="discount_avg_pct" assumptions={assumptions} onChange={onChange} />
        </div>
      </div>
      <>
        <div className="rounded-2xl border border-border/40 bg-card p-6" data-testid="entregas-comun">
          <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Entregas · aplica en ambos modos</h5>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            <AField aKey="delivery_mode" assumptions={assumptions} onChange={onChange} options={[[0,'Entrega \u00fanica (todo al final)'],[1,'Entrega progresiva']]} />
            {(assumptions?.delivery_mode === 1 || assumptions?.delivery_mode === '1') && <>
              <AField aKey="first_delivery_pct" assumptions={assumptions} onChange={handleLinkedChange} />
              <AField aKey="first_delivery_month" assumptions={assumptions} onChange={handleLinkedChange} />
            </>}
            <AField aKey="delivery_lag_months" assumptions={assumptions} onChange={onChange} />
          </div>
        </div>
      </>
      {overLimit && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-red-200 bg-red-50/50 text-sm">
          <AlertTriangle className="h-4 w-4 shrink-0" style={{color:'var(--danger)'}} />
          <span style={{color:'var(--danger)'}}>Las ventas ({Math.round(cumFinal)} uds) superan el total ({totalUnits} uds). Ajusta el programa.</span>
        </div>
      )}
      {underLimit && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg text-sm" style={{ background: 'var(--warning-soft)', border: '1px solid rgba(245,166,35,0.15)' }} data-testid="sales-under-limit-banner">
          <AlertTriangle className="h-4 w-4 shrink-0" style={{ color: 'var(--warning-color)' }} />
          <span style={{ color: 'var(--warning-color)' }}>
            El programa mensual acumula {formatNumber(cumFinal, 1)} de {totalUnits} unidades del proyecto ({Math.round((cumFinal / totalUnits) * 100)}% de las unidades): quedan {formatNumber(totalUnits - cumFinal, 1)} sin colocar. El análisis corre con lo capturado, sin repartir el faltante.
          </span>
        </div>
      )}
      <div className="rounded-xl border border-border overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: '380px' }}>
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow className="bg-muted/30 text-[11px]">
                <TableHead className="text-center w-[45px]">Mes</TableHead>
                <TableHead className="text-center w-[50px]">Fase</TableHead>
                <TableHead className="text-right w-[90px]">Uds/mes</TableHead>
                <TableHead className="text-right w-[80px]">Acumulado</TableHead>
                <TableHead className="text-right w-[70px]">% Vendido</TableHead>
                <TableHead className="w-[120px]">Avance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {salesPreview.map((r, idx) => (
                <TableRow key={r.month} className="text-xs hover:bg-accent/30">
                  <TableCell className="text-center tabular-nums font-medium">{r.month}</TableCell>
                  <TableCell className="text-center"><span className="text-[10px] px-1.5 py-0.5 rounded-full text-white font-medium" style={{ background: phaseColors[r.phase] }}>{r.phase === 'preventa' ? 'Pre' : r.phase === 'construccion' ? 'Obra' : 'Post'}</span></TableCell>
                  <TableCell className="text-right tabular-nums">
                    {mode === 'detailed' ? <TableInput value={r.units} onChange={(v) => handleManualSalesEdit(idx, v)} type="number" className="w-16" testId={`sales-mo-${r.month}`} /> : r.units > 0 ? formatNumber(r.units, 1) : '\u2014'}
                  </TableCell>
                  <TableCell className="text-right tabular-nums" style={r.cumSold > totalUnits ? {color:'var(--danger)'} : {}}>{r.cumSold > 0 ? formatNumber(r.cumSold, 0) : '\u2014'}</TableCell>
                  <TableCell className="text-right tabular-nums" style={r.pctSold > 100 ? {color:'var(--danger)'} : {}}>{r.pctSold > 0 ? `${r.pctSold}%` : '\u2014'}</TableCell>
                  <TableCell><div className="w-full h-3 bg-muted/30 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(r.pctSold, 100)}%`, background: r.pctSold > 100 ? 'var(--danger)' : 'var(--primary-hex)' }} /></div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_SEGMENTS = [
  { name: 'Bancario', pct_units: 0.50, anticipo_type: 'pct_price', anticipo_value: 0.10, parcialidades_pct: 0, saldo_lag_months: 1 },
  { name: 'Institucional', pct_units: 0.40, anticipo_type: 'fixed_amount', anticipo_value: 50000, parcialidades_pct: 0, saldo_lag_months: 2 },
  { name: 'Recurso Propio', pct_units: 0.10, anticipo_type: 'pct_price', anticipo_value: 0, parcialidades_pct: 0.50, saldo_lag_months: 0 },
];

const COLLECTION_TEMPLATES = [
  { id: 'credito_institucional', label: 'Crédito institucional', desc: 'Infonavit, Fovissste — el grueso llega al escriturar', enganche: 0.05, parcialidades: 0.05, escrituracion: 0.90 },
  { id: 'mixto_bancario', label: 'Mixto bancario', desc: 'Banca + recurso propio del cliente', enganche: 0.10, parcialidades: 0.20, escrituracion: 0.70 },
  { id: 'preventa_contado', label: 'Preventa / contado', desc: 'Clientes con liquidez, cobro temprano', enganche: 0.20, parcialidades: 0.30, escrituracion: 0.50 },
  { id: 'institucional_puro', label: 'Institucional puro', desc: 'Casi todo al final, solo apartado', enganche: 0.05, parcialidades: 0.0, escrituracion: 0.95 },
  { id: 'personalizado', label: 'Personalizado', desc: 'Captura manual sin plantilla' },
];

export function StepCollection({ assumptions, onChange, collectionStructure, scenarioId, onReload }) {
  const a = assumptions || {};
  const enganche = a.enganche_pct || 0;
  const parcialidades = a.parcialidades_pct || 0;
  const escrituracion = a.pago_escrituracion_pct || 0;
  const sumPct = enganche + parcialidades + escrituracion;
  const sumOk = Math.abs(sumPct - 1.0) < 0.005;

  // ── Segmented collection state ──
  const rawSegs = a.collection_segments_json || '';
  // SWA-214 · el toggle refleja la key, no la presencia del JSON. Con la key
  // ausente se aplica el mismo criterio de compatibilidad que el motor
  // (revenue.resolve_collection_mode): el JSON legible indica segmentada.
  const [segmented, setSegmented] = useState(() => {
    const mode = String(a.collection_capture_mode || '').trim();
    if (mode) return mode === 'segmentada';
    try { return Array.isArray(JSON.parse(rawSegs)) && JSON.parse(rawSegs).length > 0; } catch { return false; }
  });
  const [segments, setSegments] = useState(() => {
    try {
      const parsed = JSON.parse(rawSegs);
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_SEGMENTS;
    } catch { return DEFAULT_SEGMENTS; }
  });

  const segUnitSum = segments.reduce((s, seg) => s + (seg.pct_units || 0), 0);
  const segSumOk = Math.abs(segUnitSum - 1.0) < 0.005;

  const updateSegment = (idx, field, value) => {
    const next = segments.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    setSegments(next);
    onChange('collection_segments_json', JSON.stringify(next));
    // Auto-populate derived params
    autoPopulateFromSegments(next);
  };

  const autoPopulateFromSegments = (segs) => {
    // Bancario + Institucional = hipotecario, Recurso Propio = contado
    let hipotecario = 0, contado = 0;
    for (const s of segs) {
      const pct = s.pct_units || 0;
      const name = (s.name || '').toLowerCase();
      if (name.includes('propio') || name.includes('contado')) contado += pct;
      else hipotecario += pct;
    }
    onChange('credito_hipotecario_pct', Math.round(hipotecario * 10000) / 10000);
    onChange('contado_pct', Math.round(contado * 10000) / 10000);
    onChange('subsidio_pct', 0);
  };

  const toggleSegmented = (on) => {
    setSegmented(on);
    // SWA-205 · el motor ramifica por esta key, no por la presencia del JSON.
    // SWA-215 · conmutar solo cambia el modo: los canales capturados se conservan
    // para que el usuario pueda comparar los dos modos y volver.
    onChange('collection_capture_mode', on ? 'segmentada' : 'perfil_unico');
    if (on) {
      onChange('collection_segments_json', JSON.stringify(segments));
      autoPopulateFromSegments(segments);
    }
  };

  const addSegment = () => {
    const next = [...segments, { name: 'Nuevo canal', pct_units: 0, anticipo_type: 'pct_price', anticipo_value: 0, parcialidades_pct: 0, saldo_lag_months: 0 }];
    setSegments(next);
    onChange('collection_segments_json', JSON.stringify(next));
  };

  const removeSegment = (idx) => {
    const next = segments.filter((_, i) => i !== idx);
    setSegments(next);
    onChange('collection_segments_json', JSON.stringify(next));
  };

  // Detect which template matches current values
  const activeTemplate = COLLECTION_TEMPLATES.find(t =>
    t.id !== 'personalizado' &&
    Math.abs((t.enganche || 0) - enganche) < 0.005 &&
    Math.abs((t.parcialidades || 0) - parcialidades) < 0.005 &&
    Math.abs((t.escrituracion || 0) - escrituracion) < 0.005
  ) || COLLECTION_TEMPLATES.find(t => t.id === 'personalizado');

  const applyTemplate = (tmpl) => {
    if (tmpl.id === 'personalizado') return;
    onChange('enganche_pct', tmpl.enganche);
    onChange('parcialidades_pct', tmpl.parcialidades);
    onChange('pago_entrega_pct', 0);
    onChange('pago_escrituracion_pct', tmpl.escrituracion);
  };

  const handleLinkedChange = (key, value) => {
    onChange(key, value);
    if (['enganche_pct', 'parcialidades_pct'].includes(key)) {
      const a2 = { ...a, [key]: value };
      const eng = a2.enganche_pct || 0;
      const par = a2.parcialidades_pct || 0;
      const remaining = Math.max(0, 1.0 - eng - par);
      onChange('pago_escrituracion_pct', Math.round(remaining * 10000) / 10000);
    }
  };

  return (
    <div className="space-y-4">
      {/* Mode toggle */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Modo de cobranza</h5>
        <div className="grid grid-cols-2 gap-2">
          {[{ id: false, label: 'Perfil único', desc: 'Todos los compradores con la misma estructura' },
            { id: true, label: 'Cobranza segmentada', desc: 'Canales con tiempos y anticipos distintos' }].map(opt => (
            <button key={String(opt.id)} onClick={() => toggleSegmented(opt.id)}
              className="text-left p-3 rounded-xl transition-all"
              data-testid={`collection-mode-${opt.id ? 'segmented' : 'single'}`}
              style={{ background: segmented === opt.id ? 'var(--financial-light)' : '#F8FAFC', border: `1.5px solid ${segmented === opt.id ? 'var(--financial-primary)' : '#E2E8F0'}` }}>
              <div className="text-xs font-semibold" style={{ color: segmented === opt.id ? 'var(--financial-primary)' : 'var(--text-primary)' }}>{opt.label}</div>
              <div className="text-[10px] mt-0.5" style={{ color: '#94A3B8' }}>{opt.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {segmented ? (
        <>
          {/* Segment cards */}
          <div className="space-y-3">
            {segments.map((seg, idx) => (
              <div key={idx} className="rounded-2xl border border-border/40 bg-card p-5" data-testid={`segment-card-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <input className="text-sm font-semibold bg-transparent border-none outline-none" style={{ color: 'var(--text-primary)' }}
                    value={seg.name} onChange={e => updateSegment(idx, 'name', e.target.value)}
                    data-testid={`segment-name-${idx}`} />
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] tabular-nums">{Math.round((seg.pct_units || 0) * 100)}% uds</Badge>
                    {segments.length > 1 && (
                      <button onClick={() => removeSegment(idx)} className="text-xs px-1.5 py-0.5 rounded hover:bg-red-50" style={{ color: 'var(--danger)' }}
                        data-testid={`segment-remove-${idx}`}>Eliminar</button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                  <div>
                    <label className="text-[10px] font-medium block mb-1" style={{ color: '#94A3B8' }}>% de viviendas</label>
                    <div className="relative">
                      <Input type="number" step="1" min="0" max="100" className="h-8 text-xs pr-6"
                        value={Math.round((seg.pct_units || 0) * 100) || ''}
                        onChange={e => updateSegment(idx, 'pct_units', (parseFloat(e.target.value) || 0) / 100)}
                        data-testid={`segment-pct-${idx}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px]" style={{ color: '#94A3B8' }}>%</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium block mb-1" style={{ color: '#94A3B8' }}>Tipo de anticipo</label>
                    <select className="h-8 text-xs w-full rounded-md border border-input bg-background px-2"
                      value={seg.anticipo_type || 'pct_price'} onChange={e => updateSegment(idx, 'anticipo_type', e.target.value)}
                      data-testid={`segment-anticipo-type-${idx}`}>
                      <option value="pct_price">% del precio</option>
                      <option value="fixed_amount">Monto fijo ($)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium block mb-1" style={{ color: '#94A3B8' }}>
                      {seg.anticipo_type === 'fixed_amount' ? 'Monto anticipo' : 'Anticipo'}
                    </label>
                    <div className="relative">
                      {seg.anticipo_type === 'fixed_amount' ? (
                        <Input type="number" step="1000" min="0" className="h-8 text-xs pl-5"
                          value={seg.anticipo_value || ''} onChange={e => updateSegment(idx, 'anticipo_value', parseFloat(e.target.value) || 0)}
                          data-testid={`segment-anticipo-val-${idx}`} />
                      ) : (
                        <Input type="number" step="1" min="0" max="100" className="h-8 text-xs pr-6"
                          value={Math.round((seg.anticipo_value || 0) * 100) || ''}
                          onChange={e => updateSegment(idx, 'anticipo_value', (parseFloat(e.target.value) || 0) / 100)}
                          data-testid={`segment-anticipo-val-${idx}`} />
                      )}
                      <span className="absolute top-1/2 -translate-y-1/2 text-[10px]" style={{ color: '#94A3B8', ...(seg.anticipo_type === 'fixed_amount' ? { left: 8 } : { right: 8 }) }}>
                        {seg.anticipo_type === 'fixed_amount' ? '$' : '%'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium block mb-1" style={{ color: '#94A3B8' }}>Parcialidades obra</label>
                    <div className="relative">
                      <Input type="number" step="1" min="0" max="100" className="h-8 text-xs pr-6"
                        value={Math.round((seg.parcialidades_pct || 0) * 100) || ''}
                        onChange={e => updateSegment(idx, 'parcialidades_pct', (parseFloat(e.target.value) || 0) / 100)}
                        data-testid={`segment-parc-${idx}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px]" style={{ color: '#94A3B8' }}>%</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium block mb-1" style={{ color: '#94A3B8' }}>Rezago saldo</label>
                    <div className="relative">
                      <Input type="number" step="1" min="0" max="12" className="h-8 text-xs pr-10"
                        value={seg.saldo_lag_months ?? ''} onChange={e => updateSegment(idx, 'saldo_lag_months', parseInt(e.target.value) || 0)}
                        data-testid={`segment-lag-${idx}`} />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px]" style={{ color: '#94A3B8' }}>meses</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button onClick={addSegment} className="w-full p-2 rounded-xl border border-dashed text-xs font-medium transition-colors hover:bg-muted/50"
            style={{ borderColor: '#CBD5E1', color: '#64748B' }} data-testid="add-segment-btn">
            + Agregar canal
          </button>

          {/* Validation */}
          <div className={`flex items-center gap-2 p-2.5 rounded-lg text-sm`} style={{ background: segSumOk ? 'var(--success-soft)' : 'var(--warning-soft)', border: `1px solid ${segSumOk ? 'rgba(12,191,115,0.15)' : 'rgba(245,166,35,0.15)'}` }}>
            <Info className="h-4 w-4 shrink-0" style={{ color: segSumOk ? 'var(--success)' : 'var(--warning-color)' }} />
            <span style={{ color: 'var(--text-secondary)' }}>
              Suma de canales: <strong>{Math.round(segUnitSum * 100)}%</strong>
              {segSumOk ? ' — OK' : ` — debe sumar 100% de las unidades del proyecto (faltan ${Math.round((1 - segUnitSum) * 100)} pp)`}
            </span>
          </div>
        </>
      ) : (
        <>
          {/* Template selector (perfil único) */}
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Plantilla de cobranza</h5>
              <Badge variant="outline" className="text-[10px]" style={activeTemplate?.id !== 'personalizado' ? { borderColor: 'var(--success)', color: 'var(--success)' } : {}}>
                {activeTemplate?.label || 'Personalizado'}
              </Badge>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {COLLECTION_TEMPLATES.filter(t => t.id !== 'personalizado').map(tmpl => {
                const isActive = activeTemplate?.id === tmpl.id;
                return (
                  <button key={tmpl.id} onClick={() => applyTemplate(tmpl)}
                    className="text-left p-3 rounded-xl transition-all duration-150"
                    style={{ background: isActive ? 'var(--financial-light)' : '#F8FAFC', border: `1.5px solid ${isActive ? 'var(--financial-primary)' : '#E2E8F0'}` }}
                    data-testid={`template-${tmpl.id}`}>
                    <div className="text-xs font-semibold mb-0.5" style={{ color: isActive ? 'var(--financial-primary)' : 'var(--text-primary)' }}>{tmpl.label}</div>
                    <div className="text-[10px]" style={{ color: '#94A3B8' }}>{tmpl.desc}</div>
                    <div className="text-[10px] font-medium mt-1.5 tabular-nums" style={{ color: '#64748B' }}>
                      {tmpl.enganche * 100}/{tmpl.parcialidades * 100}/{tmpl.escrituracion * 100}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Editable percentages */}
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Estructura de cobranza</h5>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="apartado_amount" assumptions={assumptions} onChange={onChange} />
              <AField aKey="enganche_pct" assumptions={assumptions} onChange={handleLinkedChange} />
              <AField aKey="enganche_months" assumptions={assumptions} onChange={onChange} />
              <AField aKey="parcialidades_pct" assumptions={assumptions} onChange={handleLinkedChange} />
              <AField aKey="pago_escrituracion_pct" assumptions={assumptions} onChange={onChange} />
            </div>
            <div className={`flex items-center gap-2 p-2.5 rounded-lg mt-3 text-sm`} style={{ background: sumOk ? 'var(--success-soft)' : 'var(--warning-soft)', border: `1px solid ${sumOk ? 'rgba(12,191,115,0.15)' : 'rgba(245,166,35,0.15)'}` }}>
              <Info className="h-4 w-4 shrink-0" style={{color: sumOk ? 'var(--success)' : 'var(--warning-color)'}} />
              <span style={{ color: 'var(--text-secondary)' }}>
                Enganche ({Math.round(enganche*100)}%) + Parcialidades ({Math.round(parcialidades*100)}%) + Escrituración ({Math.round(escrituracion*100)}%) = <strong>{Math.round(sumPct*100)}%</strong>
                {sumOk ? ' — OK' : ' — debe sumar 100%'}
              </span>
            </div>
          </div>
        </>
      )}

      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Otros parámetros</h5>
        {segmented ? (
          <>
            {/* SWA-280 · mezcla de compradores (hipotecario/contado) oculta: el motor no la lee. Ver «Registro de campos ocultos y diferidos». */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="delinquency_rate_pct" assumptions={assumptions} onChange={onChange} />
            </div>
          </>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
            {/* SWA-280 · subsidio/hipotecario/contado ocultos: el motor no los lee. Lo guardado se conserva. */}
            <AField aKey="collection_delay_months" assumptions={assumptions} onChange={onChange} step="0.1" />
            <AField aKey="delinquency_rate_pct" assumptions={assumptions} onChange={onChange} />
          </div>
        )}
      </div>
    </div>
  );
}

function SplitInput({ sponsorPct, hasInvestor, onChange }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const display = Math.round(sponsorPct * 100);
  const invDisplay = 100 - display;

  return (
    <div className="flex items-center gap-2 text-[10px]">
      <span style={{ color: '#64748B' }}>S:</span>
      <Input className="h-7 w-14 text-xs text-center rounded" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
        value={editing ? raw : display}
        onFocus={() => { setEditing(true); setRaw(String(display)); }}
        onBlur={() => {
          setEditing(false);
          const val = Math.min(100, Math.max(0, parseInt(raw) || 0)) / 100;
          if (val !== sponsorPct) onChange(val);
        }}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
        onChange={e => setRaw(e.target.value)}
      />
      <span>%</span>
      {hasInvestor && <>
        <span style={{ color: '#64748B' }}>I:</span>
        <span className="px-1.5 py-0.5 rounded tabular-nums" style={{ background: '#EEF2FF' }}>{editing ? 100 - (parseInt(raw) || 0) : invDisplay}%</span>
      </>}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Embedded Capital Stack (inside Financiamiento)
   ═══════════════════════════════════════════ */

const CAPITAL_TYPES = [
  { value: 'equity_common', label: 'Equity común' },
  { value: 'preferred_equity', label: 'Equity preferente' },
  { value: 'subordinated_debt', label: 'Deuda subordinada' },
  { value: 'mezzanine', label: 'Mezzanine' },
];
const CALC_METHODS = [
  { value: 'compound', label: 'Compuesto anual' },
  { value: 'compound_monthly', label: 'Compuesto mensual' },
  { value: 'simple', label: 'Simple' },
];

function EmbeddedCapitalStack({ scenarioId, projectId }) {
  const [sponsor, setSponsor] = useState({ capital_committed: 0, promote_target: 0.25, dev_fee: 0, asset_management_fee_pct_annual: 0 });
  const [investors, setInvestors] = useState([]);
  const [waterfall, setWaterfall] = useState([]);
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!scenarioId) return;
    setSponsor({ capital_committed: 0, promote_target: 0.25, dev_fee: 0, asset_management_fee_pct_annual: 0 });
    setInvestors([]); setWaterfall([]); setSimResult(null); setLoading(true);
    structuredInputsAPI.capitalStructure.get(scenarioId).then(r => {
      const d = r.data;
      if (d.sponsor && Object.keys(d.sponsor).length) setSponsor(d.sponsor);
      if (d.investors?.length) setInvestors(d.investors);
      if (d.waterfall?.length) setWaterfall(d.waterfall);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [scenarioId]);

  // Persist
  useEffect(() => {
    if (!scenarioId || loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      structuredInputsAPI.capitalStructure.save(scenarioId, { sponsor, investors, waterfall }).catch(() => {});
    }, 1000);
  }, [sponsor, investors, waterfall]);

  // Simulate
  const simulate = useCallback(async () => {
    if (!scenarioId || !projectId) { setSimResult(null); return; }
    setSimLoading(true);
    try {
      const r = await structuredInputsAPI.capitalStructure.simulate(scenarioId, projectId, { sponsor, investors, waterfall });
      setSimResult(r.data);
    } catch { setSimResult(null); }
    setSimLoading(false);
  }, [scenarioId, projectId, sponsor, investors, waterfall]);

  useEffect(() => {
    if (!loading && scenarioId) {
      const t = setTimeout(simulate, 1500);
      return () => clearTimeout(t);
    }
  }, [sponsor, investors, waterfall, loading, simulate]);

  const addInvestor = () => setInvestors([...investors, { name: `Inversionista ${investors.length + 1}`, capital_committed: 0, capital_type: 'preferred_equity', rate_or_pref_return: { value: 0.10, calculation: 'compound' }, participation_on_excess: 0.50, capital_calls: [] }]);

  if (loading) return <div className="text-center py-8 text-sm text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Cargando estructura de capital...</div>;

  return (
    <div className="space-y-4">
      {/* Sponsor */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Sponsor</h5>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
          <AField aKey="__cs_sponsor_capital" assumptions={{ __cs_sponsor_capital: sponsor.capital_committed }} onChange={(_, v) => setSponsor({ ...sponsor, capital_committed: v })} label="Capital comprometido" unit="MXN" />
          <AField aKey="__cs_promote" assumptions={{ __cs_promote: sponsor.promote_target }} onChange={(_, v) => setSponsor({ ...sponsor, promote_target: v })} label="Promote target" unit="%" />
          <AField aKey="__cs_devfee" assumptions={{ __cs_devfee: sponsor.dev_fee }} onChange={(_, v) => setSponsor({ ...sponsor, dev_fee: v })} label="Dev fee" unit="MXN" />
          <AField aKey="__cs_assetmgmt" assumptions={{ __cs_assetmgmt: sponsor.asset_management_fee_pct_annual }} onChange={(_, v) => setSponsor({ ...sponsor, asset_management_fee_pct_annual: v })} label="Asset mgmt fee anual" unit="%" />
        </div>
      </div>

      {/* Investors */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Inversionistas</h5>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addInvestor}><Plus className="w-3 h-3" /> Agregar</Button>
        </div>
        {investors.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">Sin inversionistas (escenario autofinanciado — sponsor recibe 100%)</p>
        ) : (
          <div className="space-y-3">
            {investors.map((inv, i) => (
              <div key={i} className="rounded-xl border border-border/50 p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Input className="h-9 text-sm font-medium rounded-lg flex-1" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
                    value={inv.name || ''} onChange={e => { const u = [...investors]; u[i] = { ...inv, name: e.target.value }; setInvestors(u); }} />
                  <Select value={inv.capital_type || 'preferred_equity'} onValueChange={v => { const u = [...investors]; u[i] = { ...inv, capital_type: v }; setInvestors(u); }}>
                    <SelectTrigger className="h-9 text-xs w-[140px] rounded-lg" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}><SelectValue /></SelectTrigger>
                    <SelectContent>{CAPITAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setInvestors(investors.filter((_, j) => j !== i))}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  <AField aKey={`__inv${i}_cap`} assumptions={{ [`__inv${i}_cap`]: inv.capital_committed }} onChange={(_, v) => { const u = [...investors]; u[i] = { ...inv, capital_committed: v }; setInvestors(u); }} label="Monto comprometido" unit="MXN" />
                  <AField aKey={`__inv${i}_rate`} assumptions={{ [`__inv${i}_rate`]: inv.rate_or_pref_return?.value }} onChange={(_, v) => { const u = [...investors]; u[i] = { ...inv, rate_or_pref_return: { ...inv.rate_or_pref_return, value: v } }; setInvestors(u); }} label="Tasa / Pref return" unit="%" />
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium" style={{ color: '#64748B' }}>Cálculo</span>
                    <Select value={inv.rate_or_pref_return?.calculation || 'compound'} onValueChange={v => { const u = [...investors]; u[i] = { ...inv, rate_or_pref_return: { ...inv.rate_or_pref_return, calculation: v } }; setInvestors(u); }}>
                      <SelectTrigger className="h-11 text-base font-semibold rounded-xl" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}><SelectValue /></SelectTrigger>
                      <SelectContent>{CALC_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Capital calls */}
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #E2E8F0' }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Llamadas de capital</span>
                    {(() => { const callSum = (inv.capital_calls || []).reduce((s, c) => s + (c.amount || 0), 0); const committed = inv.capital_committed || 0;
                      return callSum > 0 && Math.abs(callSum - committed) > 1 ? <span className="text-[10px]" style={{ color: 'var(--warning-color)' }}>Llamadas ${callSum.toLocaleString('es-MX')} ≠ comprometido ${committed.toLocaleString('es-MX')}</span> : null;
                    })()}
                  </div>
                  <div className="space-y-1.5">
                    {(inv.capital_calls || []).map((c, ci) => (
                      <div key={ci} className="flex items-center gap-3">
                        <span className="text-xs shrink-0" style={{ color: '#64748B' }}>Mes</span>
                        <Input className="h-8 w-16 text-sm text-center rounded-lg" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
                          value={c.month || ''} onChange={e => { const calls = [...(inv.capital_calls || [])]; calls[ci] = { ...c, month: parseInt(e.target.value) || 0 }; const u = [...investors]; u[i] = { ...inv, capital_calls: calls }; setInvestors(u); }} />
                        <span className="text-xs shrink-0" style={{ color: '#64748B' }}>Monto</span>
                        <FormattedInput value={c.amount || 0} onChange={v => { const calls = [...(inv.capital_calls || [])]; calls[ci] = { ...c, amount: v }; const u = [...investors]; u[i] = { ...inv, capital_calls: calls }; setInvestors(u); }} isCurrency className="flex-1" />
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={() => { const calls = (inv.capital_calls || []).filter((_, j) => j !== ci); const u = [...investors]; u[i] = { ...inv, capital_calls: calls }; setInvestors(u); }}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                  <Button variant="outline" size="sm" className="mt-2 h-7 text-xs gap-1" onClick={() => { const calls = [...(inv.capital_calls || []), { month: 1, amount: 0 }]; const u = [...investors]; u[i] = { ...inv, capital_calls: calls }; setInvestors(u); }}>
                    <Plus className="w-3 h-3" /> Agregar llamada
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Waterfall Hurdles */}
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <div className="flex items-center justify-between mb-3">
          <h5 className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#94A3B8' }}>Waterfall de distribución</h5>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => setWaterfall([...waterfall, { order: waterfall.length + 1, concept: '', recipients: ['sponsor'], amount_calculation: 'capital_returned', residual_split: {} }])}>
            <Plus className="w-3 h-3" /> Agregar hurdle
          </Button>
        </div>
        {waterfall.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">Sin hurdles configurados. Agrega al menos devolución de capital y split residual.</p>
        ) : (
          <div className="space-y-2">
            {waterfall.map((h, i) => (
              <div key={i} className="flex items-center gap-2 p-3 rounded-lg" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                <div className="flex flex-col gap-0.5 shrink-0">
                  <button disabled={i === 0} onClick={() => { const arr = [...waterfall]; const [moved] = arr.splice(i, 1); arr.splice(i - 1, 0, moved); setWaterfall(arr.map((x, j) => ({ ...x, order: j + 1 }))); }} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronUp className="w-3 h-3" /></button>
                  <button disabled={i === waterfall.length - 1} onClick={() => { const arr = [...waterfall]; const [moved] = arr.splice(i, 1); arr.splice(i + 1, 0, moved); setWaterfall(arr.map((x, j) => ({ ...x, order: j + 1 }))); }} className="text-muted-foreground hover:text-foreground disabled:opacity-20"><ChevronDown className="w-3 h-3" /></button>
                </div>
                <Badge variant="outline" className="text-[10px] w-6 h-6 flex items-center justify-center rounded-full shrink-0">{i + 1}</Badge>

                <Input className="h-8 text-xs font-medium rounded-lg flex-1" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}
                  value={h.concept || ''} onChange={e => { const u = [...waterfall]; u[i] = { ...h, concept: e.target.value }; setWaterfall(u); }} placeholder="Concepto" />

                <Select value={h.amount_calculation || 'capital_returned'} onValueChange={v => { const u = [...waterfall]; u[i] = { ...h, amount_calculation: v }; setWaterfall(u); }}>
                  <SelectTrigger className="h-8 text-xs w-[140px] rounded-lg" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="capital_returned">Capital returned</SelectItem>
                    <SelectItem value="pref_return">Pref return</SelectItem>
                    <SelectItem value="catch_up">Catch-up</SelectItem>
                    <SelectItem value="residual_split">Residual split</SelectItem>
                  </SelectContent>
                </Select>

                {/* Recipient selector (except residual_split which uses its own split %) */}
                {h.amount_calculation !== 'residual_split' && (
                  <Select value={(h.recipients || ['sponsor'])[0]} onValueChange={v => { const u = [...waterfall]; u[i] = { ...h, recipients: [v] }; setWaterfall(u); }}>
                    <SelectTrigger className="h-8 text-xs w-[120px] rounded-lg" style={{ background: '#EEF2FF', border: '1px solid #E2E8F0' }}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sponsor">Sponsor</SelectItem>
                      {investors.map((inv, j) => <SelectItem key={j} value={`investor_${j+1}`}>{inv.name || `Inv ${j+1}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}

                {h.amount_calculation === 'catch_up' && (
                  <AField aKey={`__h${i}_target`} assumptions={{ [`__h${i}_target`]: h.target }} onChange={(_, v) => { const u = [...waterfall]; u[i] = { ...h, target: v }; setWaterfall(u); }} label="" unit="%" className="w-20" />
                )}

                {h.amount_calculation === 'residual_split' && (
                  <SplitInput
                    sponsorPct={h.residual_split?.sponsor ?? (investors.length === 0 ? 1 : 0.5)}
                    hasInvestor={investors.length > 0}
                    onChange={(sp) => {
                      const invPct = Math.round((1 - sp) * 100) / 100;
                      const newSplit = { sponsor: sp };
                      if (investors.length > 0) newSplit.investor_1 = invPct;
                      const u = [...waterfall]; u[i] = { ...h, residual_split: newSplit }; setWaterfall(u);
                    }}
                  />
                )}

                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive shrink-0" onClick={() => setWaterfall(waterfall.filter((_, j) => j !== i).map((x, j) => ({ ...x, order: j + 1 })))}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Simulation result */}
      {simResult && (
        <div className="rounded-2xl border border-border/40 bg-card p-6">
          <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Simulación de distribución</h5>
          <div className="text-lg font-display font-semibold tabular-nums mb-3">{formatCurrency(simResult.distributable_pool)}<span className="text-xs font-normal ml-2" style={{ color: '#94A3B8' }}>Pool distribuible</span></div>
          <div className="space-y-2">
            {Object.entries(simResult.party_metrics || {}).map(([key, pm]) => (
              <div key={key} className="flex items-center justify-between p-3 rounded-lg" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                <span className="text-xs font-semibold">{pm.name || key}</span>
                <div className="flex items-center gap-4 text-xs tabular-nums">
                  <span>{formatCurrency(pm.total_received)}</span>
                  {pm.moic && <span className="font-semibold">MOIC {pm.moic}x</span>}
                  {pm.irr_annual != null && <span style={{ color: '#94A3B8' }}>TIR ~{pm.irr_annual}%</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {simLoading && <div className="text-center py-4 text-xs text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" />Calculando...</div>}
    </div>
  );
}

export function StepFinancing({ assumptions, onChange, scenarioId, projectId, onReload }) {
  const rawMode = assumptions?.bridge_loan_mode ?? 0;
  // Mode 3 (Ministraciones por avance) es GHOST en motor v2.5.0: el engine pre-computa
  // la curva pero NUNCA la consume — comportamiento idéntico al modo 1. Decisión FASE 2
  // (Arturo, 2026-02): ocultar de la UI y aliasarlo silenciosamente a modo 1. Cuando el
  // usuario guarde, el valor persistirá como 1 (limpio en BD).
  const mode = rawMode === 3 ? 1 : rawMode;
  // SWA-194 · el aforo ya no tiene default: si hay crédito y está vacío/0, es un
  // dato PENDIENTE (el motor no dispone y emite alerta crítica), no un cero.
  const aforoPendiente = Number(rawMode) !== 0 && !(parseFloat(assumptions?.credit_aforo_pct) > 0);
  // Anticipo: el motor da precedencia al monto sobre el porcentaje. La UI hace
  // imposible el conflicto — elegir un modo limpia el otro campo.
  const advAmount = parseFloat(assumptions?.credit_advance_amount) || 0;
  const advPct = parseFloat(assumptions?.credit_advance_pct) || 0;
  // La elección del usuario manda mientras el campo esté en 0 (si no, elegir
  // "Por monto" con monto 0 volvería a "Sin anticipo" y no se podría capturar).
  const [advChoice, setAdvChoice] = useState(null);
  const advMode = advChoice ?? (advAmount > 0 ? 'amount' : (advPct > 0 ? 'pct' : 'none'));
  const setAdvMode = (next) => {
    setAdvChoice(next);
    if (next === 'none') { onChange?.('credit_advance_amount', 0); onChange?.('credit_advance_pct', 0); }
    if (next === 'amount') { onChange?.('credit_advance_pct', 0); }
    if (next === 'pct') { onChange?.('credit_advance_amount', 0); }
  };
  // Auto-migrate persisted ghost mode on next save:
  useEffect(() => {
    if (rawMode === 3) {
      onChange?.('bridge_loan_mode', 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMode]);

  const rateMode = assumptions?.interest_rate_mode ?? 0;
  const [csTab, setCsTab] = useState('deuda'); // deuda | capital | pasivos

  // Derived preset (read-only mirror of the bridge mode)
  const activePreset = presetFromBridgeMode(mode);

  return (
    <div className="space-y-4">
      {/* Tab toggle: Deuda vs Estructura de Capital vs Pasivos Existentes */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setCsTab('deuda')} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${csTab === 'deuda' ? 'text-white' : ''}`} style={{ background: csTab === 'deuda' ? 'var(--financial-primary)' : 'var(--bg-elevated)', color: csTab === 'deuda' ? 'white' : '#64748B' }} data-testid="fin-tab-deuda">
          Crédito del Proyecto
        </button>
        <button onClick={() => setCsTab('capital')} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${csTab === 'capital' ? 'text-white' : ''}`} style={{ background: csTab === 'capital' ? 'var(--financial-primary)' : 'var(--bg-elevated)', color: csTab === 'capital' ? 'white' : '#64748B' }} data-testid="fin-tab-capital">
          Estructura de Capital
        </button>
        <button onClick={() => setCsTab('pasivos')} className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all ${csTab === 'pasivos' ? 'text-white' : ''}`} style={{ background: csTab === 'pasivos' ? 'var(--financial-primary)' : 'var(--bg-elevated)', color: csTab === 'pasivos' ? 'white' : '#64748B' }} data-testid="fin-tab-pasivos">
          Pasivos Existentes
        </button>
      </div>

      {csTab === 'deuda' && (
        <>
          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Estructura de financiamiento</h5>
            {!(Number(assumptions?.credit_line_amount) > 0) && (
              <div className="mb-3 p-2.5 rounded-lg border text-xs" style={{ borderColor: '#FCD34D', background: 'rgba(253,230,138,0.15)', color: '#92400E' }} data-testid="sin-linea-credito">
                Sin línea de crédito: el proyecto se modela 100 % con capital propio y sin tasa. Lo capturado en recurso propio y tasa se conserva para cuando declares una línea.
              </div>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="equity_pct" assumptions={assumptions} onChange={onChange}
                note={!(Number(assumptions?.credit_line_amount) > 0) ? 'No aplica sin línea de crédito' : ''} />
              <AField
                aKey="bridge_loan_mode"
                assumptions={assumptions}
                onChange={onChange}
                options={PROJECT_LOAN_PRESETS
                  // Filtra presets ocultos (legacy) excepto si el escenario actual los tiene seleccionados
                  .filter(p => !p.hidden || Number(assumptions?.bridge_loan_mode ?? 1) === p.bridge_loan_mode)
                  .map(p => [p.bridge_loan_mode, p.label])}
                data-testid="bridge-loan-preset"
              />
              <AField aKey="credit_line_amount" assumptions={assumptions} onChange={onChange} />
              {/* SWA-194 · El aforo gobierna la línea completa (SWA-54): visible en
                  todos los modos con crédito y marcado como dato pendiente si falta. */}
              {mode !== 0 && (
                <div data-testid="aforo-credito-wrap">
                  <AField
                    aKey="credit_aforo_pct"
                    assumptions={assumptions}
                    onChange={onChange}
                    required
                    forceShow
                    label="Aforo del crédito (%) ·  obligatorio"
                    tooltip="Lo declara el banco. Sin aforo no se calcula la línea. Es el porcentaje del presupuesto autorizado que el banco financia."
                    className={aforoPendiente ? 'rounded-xl ring-2 ring-amber-400 p-2' : ''}
                  />
                  {aforoPendiente && (
                    <div className="mt-1 text-[10px] font-semibold leading-snug" style={{ color: '#B45309' }} data-testid="aforo-pendiente-hint">
                      Dato pendiente · lo declara el banco. Sin aforo el motor no dispone crédito.
                    </div>
                  )}
                </div>
              )}
              {/* SWA-194 · El LTV dejó de recortar en SWA-54: es razón de cobertura. */}
              <AField
                aKey="credit_ltv_max"
                assumptions={assumptions}
                onChange={onChange}
                label="Aforo de garantía (cobertura)"
                tooltip="Razón de cobertura que exige el banco sobre el valor de las garantías. Se vigila y se avisa; no limita la ministración."
              />
              {mode === 1 && <AField aKey="ministration_lag_months" assumptions={assumptions} onChange={onChange} />}
              {(mode === 2 || mode === 4) && <>
                <AField aKey="loan_term_months" assumptions={assumptions} onChange={onChange} />
                <AField aKey="loan_grace_months" assumptions={assumptions} onChange={onChange} />
                <AField aKey="loan_disbursement_month" assumptions={assumptions} onChange={onChange} />
              </>}
            </div>

            {/* SWA-194 · Anticipo de línea · una sola decisión, sin conflicto posible */}
            {mode !== 0 && (
              <div className="mt-4 rounded-xl border p-4 space-y-3" style={{ borderColor: 'rgba(99,102,241,0.25)', background: 'rgba(99,102,241,0.03)' }} data-testid="anticipo-panel">
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--financial-primary)' }}>
                  Anticipo de línea
                </div>
                <div className="flex flex-wrap gap-2">
                  {[['none', 'Sin anticipo'], ['amount', 'Por monto'], ['pct', 'Por porcentaje']].map(([val, lbl]) => (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setAdvMode(val)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
                      style={{
                        background: advMode === val ? 'var(--financial-primary)' : 'var(--bg-elevated)',
                        color: advMode === val ? 'white' : '#64748B',
                        border: '1px solid #E2E8F0',
                      }}
                      data-testid={`anticipo-mode-${val}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                {advMode !== 'none' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                    {advMode === 'amount' && (
                      <AField aKey="credit_advance_amount" assumptions={assumptions} onChange={onChange} forceShow />
                    )}
                    {advMode === 'pct' && (
                      <AField aKey="credit_advance_pct" assumptions={assumptions} onChange={onChange} forceShow />
                    )}
                    <AField
                      aKey="credit_advance_treatment"
                      assumptions={assumptions}
                      onChange={onChange}
                      forceShow
                      options={[[0, 'Consume línea'], [1, 'Se amortiza']]}
                      data-testid="anticipo-treatment"
                    />
                    <div className="md:col-span-2 text-[10px] leading-snug" style={{ color: '#64748B' }} data-testid="anticipo-treatment-help">
                      {Number(assumptions?.credit_advance_treatment ?? 1) === 1
                        ? 'Se amortiza → Cada ministración retiene la proporción del anticipo hasta absorberlo.'
                        : 'Consume línea → El anticipo reduce la línea disponible. Las ministraciones reponen el avance completo.'}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Sprint 2 · v2.8.1 — Custom Ministration (mode=5): calendario del banco + repago flexible */}
            {mode === 5 && (
              <div className="mt-4 rounded-xl border p-4 space-y-3" style={{ borderColor: 'rgba(99,102,241,0.35)', background: 'rgba(99,102,241,0.03)' }} data-testid="custom-min-panel">
                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: 'var(--financial-primary)' }}>
                  Calendario del banco (Sprint 2 · v2.8.1)
                </div>
                <MonthlyScheduleEditor
                  label="Ministraciones del banco"
                  helpText="Cada fila = una disposición mensual. La suma no puede exceder la línea autorizada."
                  value={assumptions?.custom_ministration_json || ''}
                  onChange={(json) => onChange?.('custom_ministration_json', json)}
                  moneyLabel="Ministración"
                  testId="custom-ministration"
                  color="#4F46E5"
                  bg="rgba(79,70,229,0.03)"
                  sumTarget={parseFloat(assumptions?.credit_line_amount) || 0}
                  sumTargetLabel="Línea autorizada"
                  maxMonth={parseInt(assumptions?.commercialization_months) + parseInt(assumptions?.construction_months) + 12 || 60}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <AField
                    aKey="bridge_repayment_mode"
                    assumptions={assumptions}
                    onChange={onChange}
                    options={[
                      [EMPTY_OPTION_VALUE, 'Auto (por preset)'],
                      ['sweep', 'Sweep · barrido de caja'],
                      ['lpu', 'LPU · por unidad entregada'],
                      ['bullet', 'Bullet · todo al vencimiento'],
                      ['mixed', 'Mixto · LPU + bullet residual'],
                      ['manual', 'Manual · tabla mes / monto'],
                    ]}
                    data-testid="bridge-repayment-mode"
                  />
                  {(['lpu', 'mixed'].includes(assumptions?.bridge_repayment_mode || '')) && (
                    <AField aKey="bridge_lpu_amount" assumptions={assumptions} onChange={onChange} />
                  )}
                  {(['bullet', 'mixed'].includes(assumptions?.bridge_repayment_mode || '')) && (
                    <AField aKey="bridge_maturity_month" assumptions={assumptions} onChange={onChange} />
                  )}
                </div>
                {(assumptions?.bridge_repayment_mode === 'manual') && (
                  <MonthlyScheduleEditor
                    label="Calendario de pagos manual"
                    helpText="Fecha exacta y monto de cada pago del puente. La suma ideal iguala la línea autorizada + intereses acumulados."
                    value={assumptions?.bridge_repayment_schedule_json || ''}
                    onChange={(json) => onChange?.('bridge_repayment_schedule_json', json)}
                    moneyLabel="Pago"
                    testId="bridge-repayment-schedule"
                    color="#DC2626"
                    bg="rgba(220,38,38,0.03)"
                    maxMonth={parseInt(assumptions?.commercialization_months) + parseInt(assumptions?.construction_months) + 12 || 60}
                  />
                )}
              </div>
            )}
            {/* Read-only derived axes (taxonomy: repayment + interest) */}
            <div className="mt-4 rounded-xl border border-dashed p-3" style={{ borderColor: 'rgba(99,102,241,0.25)', background: 'rgba(99,102,241,0.04)' }} data-testid="preset-derived-axes">
              <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--financial-primary)' }}>
                Comportamiento derivado del preset (read-only)
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px]" style={{ color: '#94A3B8' }}>Esquema de repago</div>
                  <div className="font-medium" style={{ color: '#1E293B' }} data-testid="derived-repayment-label">
                    {REPAYMENT_MODES[activePreset.repayment]}
                  </div>
                </div>
                <div>
                  <div className="text-[10px]" style={{ color: '#94A3B8' }}>Tratamiento de interés</div>
                  <div className="font-medium" style={{ color: '#1E293B' }} data-testid="derived-interest-label">
                    {INTEREST_TREATMENTS[activePreset.interest]}
                  </div>
                </div>
              </div>
              <div className="text-[10px] mt-2" style={{ color: '#64748B' }}>{activePreset.description}</div>
            </div>
          </div>

          {/* Sprint 2b · v2.8.2 · Aportes de capital propio declarados por mes */}
          <div className="rounded-2xl border border-border/40 bg-card p-6" data-testid="equity-schedule-panel">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#94A3B8' }}>
              Aportes de capital declarados
            </h5>
            <p className="text-[11px] mb-3" style={{ color: '#64748B' }}>
              Declara explícitamente cuánto vas a aportar en cada mes. Elimina el hack de
              forzar <code className="text-[10px] px-1 rounded" style={{ background: '#E2E8F0' }}>equity_pct</code>.
              Si el proyecto necesita más de lo declarado, el motor lo marca como <em>equity overflow</em>.
            </p>
            <MonthlyScheduleEditor
              label="Aportes de equity declarados"
              helpText="Vacío = motor calcula solo (equity_pct sobre costos + overflow automático)."
              value={assumptions?.equity_schedule_json || ''}
              onChange={(json) => onChange?.('equity_schedule_json', json)}
              moneyLabel="Aporte"
              testId="equity-schedule"
              color="#059669"
              bg="rgba(5,150,105,0.03)"
              maxMonth={parseInt(assumptions?.commercialization_months) + parseInt(assumptions?.construction_months) + 12 || 60}
            />
          </div>

          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Tasa de interés</h5>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="interest_rate_mode" assumptions={assumptions} onChange={onChange} options={[[0,'Fija'],[1,'Variable (TIIE + spread)']]} />
              {rateMode === 0 && <AField aKey="loan_interest_rate_annual" assumptions={assumptions} onChange={onChange}
                note={!(Number(assumptions?.credit_line_amount) > 0) ? 'No aplica sin línea de crédito' : ''} />}
              {rateMode === 1 && <>
                <AField aKey="tiie_base_rate" assumptions={assumptions} onChange={onChange} />
                <AField aKey="tiie_spread" assumptions={assumptions} onChange={onChange} />
                <AField aKey="tiie_annual_change" assumptions={assumptions} onChange={onChange} step="0.001" />
              </>}
            </div>
          </div>

          <div className="rounded-2xl border border-border/40 bg-card p-6" data-testid="card-discount-rates">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Tasas de descuento</h5>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="discount_rate" label="Tasa de descuento del proyecto" unit="%" assumptions={assumptions} onChange={onChange} />
              <AField aKey="equity_required_return" label="Rendimiento exigido al capital" unit="%" assumptions={assumptions} onChange={onChange} />
              <AField aKey="mirr_reinvest_rate" label="Tasa de reinversión (MIRR)" unit="%" assumptions={assumptions} onChange={onChange} />
            </div>
            <p className="text-[11px] mt-3" style={{ color: '#64748B' }}>
              La tasa del proyecto descuenta el VAN del proyecto (sin crédito). El rendimiento exigido descuenta el VAN del inversionista y financia la MIRR; la tasa de reinversión es la que la MIRR aplica a las distribuciones. Con 0, ambas usan la tasa del proyecto. Mes 1 = hoy.
            </p>
          </div>

          <div className="rounded-2xl border border-border/40 bg-card p-6">
            <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Comisiones y gastos</h5>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              <AField aKey="comision_apertura_pct" assumptions={assumptions} onChange={onChange} />
              <AField aKey="gastos_notariales_pct" assumptions={assumptions} onChange={onChange} />
              <AField aKey="seguros_ruv_pct" assumptions={assumptions} onChange={onChange} info />
            </div>
            <p className="text-[11px] mt-3" style={{ color: '#64748B' }} data-testid="comisiones-base">
              Apertura y notariales se calculan sobre la línea autorizada y se cobran en el mes de la primera disposición.
            </p>
          </div>
        </>
      )}

      {csTab === 'capital' && scenarioId && (
        <EmbeddedCapitalStack scenarioId={scenarioId} projectId={projectId} />
      )}

      {csTab === 'pasivos' && scenarioId && (
        <>
          <ExistingLiabilitiesPanel scenarioId={scenarioId} legacyExistingBalance={Number(assumptions?.existing_loan_balance) || 0} />

          {/* Sprint 4 · v2.9.0 · Waterfall multi-tranche (Sponsor + LP1/LP2/N) */}
          <WaterfallPanel scenarioId={scenarioId} />
        </>
      )}
    </div>
  );
}

export function StepAssumptions({ assumptions, onChange }) {
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Supuestos macro</h5>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
          <AField aKey="target_irr" assumptions={assumptions} onChange={onChange} />
          <AField aKey="inflation_general_pct" assumptions={assumptions} onChange={onChange} />
        </div>
      </div>
      <div className="rounded-2xl border border-border/40 bg-card p-6">
        <h5 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#94A3B8' }}>Rangos de sensibilidad</h5>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3">
          <AField aKey="sensitivity_price_range" assumptions={assumptions} onChange={onChange} />
          <AField aKey="sensitivity_cost_range" assumptions={assumptions} onChange={onChange} />
          <AField aKey="sensitivity_rate_range" assumptions={assumptions} onChange={onChange} />
          <AField aKey="sensitivity_absorption_range" assumptions={assumptions} onChange={onChange} />
        </div>
      </div>
    </div>
  );
}

export function StepReview({ assumptions, structured }) {
  const sections = [
    { label: 'Tipologías', count: structured?.typologies?.length || 0, icon: Layers },
    { label: 'Pagos Terreno', count: structured?.land_schedule?.length || 0, icon: MapPin },
    { label: 'Presupuesto', count: structured?.budget_items?.length || 0, icon: HardHat },
    { label: 'Hitos', count: structured?.milestones?.length || 0, icon: CalendarClock },
    { label: 'Indirectos', count: structured?.indirect_costs?.length || 0, icon: Receipt },
    { label: 'Cobranza', count: structured?.collection_structure?.length || 0, icon: Banknote },
  ];
  const a = assumptions || {};
  const totalUnits = a.total_units || 0;
  const avgPrice = a.avg_price_per_unit || 0;

  // Budget: respect capture mode hierarchy (same as WizardFooter)
  const budgetMode = a.budget_capture_mode || 'general';
  const budgetItems = structured?.budget_items || [];
  let totalBudget;
  if (budgetMode === 'detailed' && budgetItems.length > 0) {
    totalBudget = budgetItems.filter(b => b.is_hard_cost !== false).reduce((s, b) => s + (b.total_amount || 0), 0);
  } else {
    const override = parseFloat(a.hard_cost_total_override) || 0;
    const hardPerUnit = a.hard_cost_per_unit || 0;
    let hardTotal = override > 0 ? override : hardPerUnit * totalUnits;
    // Urbanization amount is additive
    const urbAmount = parseFloat(a.hard_cost_urbanizacion_amount) || 0;
    if (urbAmount > 0) hardTotal += urbAmount;
    const softTotal = hardTotal * (a.soft_cost_pct || 0);
    const contingency = (hardTotal + softTotal) * (a.contingency_pct || 0);
    totalBudget = hardTotal + softTotal + contingency + (a.development_fee || 0);
  }

  // Coherence checks
  const coherenceChecks = [];
  const mkt = a.marketing_budget_pct || 0;
  if (mkt > 0 && (mkt < 0.005 || mkt > 0.08))
    coherenceChecks.push({ type: 'warning', msg: `Marketing ${(mkt*100).toFixed(1)}% fuera de rango típico (0.5%–8%)` });
  const comm = a.sales_commission_pct || 0;
  if (comm > 0 && (comm < 0.02 || comm > 0.07))
    coherenceChecks.push({ type: 'warning', msg: `Comisión de ventas ${(comm*100).toFixed(1)}% fuera de rango típico (2%–7%)` });
  const soft = a.soft_cost_pct || 0;
  if (soft > 0 && (soft < 0.02 || soft > 0.15))
    coherenceChecks.push({ type: 'warning', msg: `Costos blandos ${(soft*100).toFixed(1)}% fuera de rango típico (2%–15%)` });
  const prefRet = a.preferred_return_annual || 0;
  if (prefRet > 0 && (prefRet < 0.06 || prefRet > 0.15))
    coherenceChecks.push({ type: 'warning', msg: `Preferred return ${(prefRet*100).toFixed(1)}% fuera de rango típico (6%–15%)` });
  const absorption = a.absorption_rate_units_per_month || 0;
  if (absorption > 0 && totalUnits > 0 && totalUnits / absorption > 60)
    coherenceChecks.push({ type: 'warning', msg: `A ${absorption} uds/mes, se necesitan ${Math.round(totalUnits / absorption)} meses para vender todo (>5 años)` });
  const equity = a.equity_pct || 0;
  if (equity > 0 && equity < 0.15)
    coherenceChecks.push({ type: 'warning', msg: `Equity de ${(equity*100).toFixed(0)}% es bajo. Bancos típicamente exigen 25%+` });
  const typoUnits = (structured?.typologies || []).reduce((s, t) => s + (t.quantity || 0), 0);
  if (typoUnits > 0 && totalUnits > 0 && typoUnits !== totalUnits)
    coherenceChecks.push({ type: 'warning', msg: `Tipologías suman ${typoUnits} uds pero total es ${totalUnits} uds` });

  // L-97 · la misma fuente que alimenta el banner rojo de este paso.
  const validationIssues = buildIssues(assumptions, structured, useRunHorizon());

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card className="rounded-xl border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Unidades</p>
            <p className="text-2xl font-display font-semibold tabular-nums mt-1">{totalUnits}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Ingreso Bruto</p>
            <p className="text-xl font-display font-semibold tabular-nums mt-1">{formatCurrency(totalUnits * avgPrice)}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border/50">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Presupuesto</p>
            <p className="text-xl font-display font-semibold tabular-nums mt-1">{formatCurrency(totalBudget)}</p>
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {sections.map((s) => (
          <div key={s.label} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
            <s.icon className="h-4 w-4 text-muted-foreground shrink-0" />
            <div>
              <p className="text-sm font-medium">{s.label}</p>
              <p className="text-xs text-muted-foreground tabular-nums">{s.count} {s.count === 1 ? 'elemento' : 'elementos'}</p>
            </div>
            <CheckCircle2 className="h-4 w-4 ml-auto shrink-0" style={{ color: s.count > 0 ? 'var(--success)' : 'var(--border)' }} />
          </div>
        ))}
      </div>

      {/* Sprint 6 · Validación cruzada estructurada */}
      <div className="rounded-2xl border border-border/40 bg-card p-5 space-y-3">
        <h5 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: '#94A3B8' }}>
          <ShieldCheck className="w-4 h-4" /> Validación pre-análisis
        </h5>
        <WizardValidationBanner assumptions={assumptions} structured={structured} />
      </div>

      {/* Coherence checks */}
      {coherenceChecks.length > 0 && (
        <div className="rounded-2xl border border-border/40 bg-card p-5">
          <h5 className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: '#94A3B8' }}>
            <ShieldCheck className="w-4 h-4" /> Verificación de coherencia
          </h5>
          <div className="space-y-2">
            {coherenceChecks.map((c, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs" style={{ background: 'rgba(245,166,35,0.06)', border: '1px solid rgba(245,166,35,0.12)' }}>
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--warning-color)' }} />
                <span style={{ color: '#92400E' }}>{c.msg}</span>
              </div>
            ))}
          </div>
          <p className="text-[10px] mt-3" style={{ color: '#94A3B8' }}>Estas advertencias no bloquean el análisis. Son rangos típicos de la industria para referencia.</p>
        </div>
      )}
      {/* L-97 · el verde consulta la misma fuente que el rojo. Su condición era
          sólo `coherenceChecks` —las 7 reglas locales de este paso—, así que se
          pintaba "todos los parámetros están dentro de rangos típicos" junto al
          crítico que buildIssues ya mostraba en el banner de arriba. Ahora exige
          que AMBAS fuentes estén vacías: el mensaje sólo aparece cuando es
          verdadero. Cuando hay issues no se sustituye por otro texto: el
          WizardValidationBanner de esta misma tarjeta ya los enumera con su
          severidad y su precedencia (SWA-237), y una tercera voz sería el mismo
          defecto al revés. */}
      {coherenceChecks.length === 0 && validationIssues.length === 0 && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm" style={{ background: 'rgba(12,191,115,0.06)', border: '1px solid rgba(12,191,115,0.15)' }} data-testid="review-all-clear">
          <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: 'var(--success)' }} />
          <span style={{ color: 'var(--success)' }}>Todos los parámetros están dentro de rangos típicos. Listo para ejecutar análisis.</span>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   MAIN WIZARD COMPONENT
   ════════════════════════════════════════════════════════════════ */
